// Intuition Canvas v4 — dark, research-forward. Rule: rust (--mirror) is the mirror's trace,
// never an accent. Colour appears only where the AI is present or acting; every user affordance is achromatic ink.
import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, memo, Fragment } from "react";
import { PRIVACY } from "./privacyCopy";
import { feltResonance, freezeArrangement, frozenGeometry, arrangementChanged } from "./freeze";
import { snapshotLabel, divergence } from "./divergence";
import { classifyFailure } from "./readingFailure";
import { readingCacheKey, cacheRead, cacheWrite } from "./readingCache";
import { SEED_READING, SEED_LENS, SEED_LANGUAGE, seedFragments, SEED_PROTOCOL_VERSION, SEED_MODEL_VERSION } from "./seed";
import { displayVersion } from "./version";
import { layoutReading, toPixels } from "./modelLayout";
import { buildExport, serializeExport, exportFilename, parseImport, restorableRecords, restoreFragments } from "./exportRecord";
import { phaseAt, isAsleep, createEditLog } from "./session";
import { buildReadingContent } from "./readingRequest";

// Offline demo toggle (local dev only). In production this is unset, so the app calls the
// server-side proxy at /api/mirror, which holds the Anthropic key. The key never reaches the client.
const MOCK = process.env.REACT_APP_MOCK === "true";


// A projection is only real if it arrived in the mirror's shape. A malformed
// palette (bad hex) or a missing layer means the mirror didn't answer — treat
// it as a failure rather than rendering garbage into the panel.
// Structured outputs guarantee the schema at the API boundary, so this is not a
// parser: it only catches a reply that never came from that boundary at all
// (a proxy error page, a truncated body). An empty relations array is VALID —
// refusal is a reading.
const isValidReading = (p) =>
  !!p &&
  Array.isArray(p.relations) &&
  Array.isArray(p.unlinked) &&
  Array.isArray(p.fragments) &&
  p.relations.every((r) => typeof r.a === "string" && typeof r.b === "string" && (r.fit === "strong" || r.fit === "weak"));

// ── design tokens (inherited from the portfolio system) ─────────
const T = {
  bg: "#111110", bg2: "#1A1917", bg3: "#0C0C0B", bgDeep: "#1A1917",
  ink: "#E8E6E0", inkMid: "#9A9890",
  // inkLow is the dimmest tier TEXT may use: 6.1:1 on bg, 5.7:1 on bg2. It was
  // #84827A (4.91 / 4.56) — over the AA floor by hundredths, which is a pass on
  // paper and a squint on a screen. Methodology and status lines all sit in this
  // tier, so raising the token raises every one of them at once.
  // No line token was split off: audited at v6.1.4 and inkLow had no line duty
  // to separate. Every rule, ring and hairline already draws from inkFaint, so
  // raising inkLow moves text only and leaves the drawn marks where they were.
  // inkFaint is for LINES ONLY — rings, grid, hairline borders, tiny glyph marks.
  // It measures 2.65:1, so putting a sentence in it makes that sentence unreadable.
  inkLow: "#95938B", inkFaint: "#5A5850",
  border: "rgba(232,230,224,0.14)", borderFaint: "rgba(232,230,224,0.08)",
  mirror: "#C4885A", // rust: the AI's output/activity/trace only (§8)
  // aligned with the portfolio (DESIGN.md): Fraunces for display, Geist for
  // everything else. Fraunces slots must go through fraunces() below.
  display: "'Fraunces', Georgia, serif",
  mono: "'Geist Mono', ui-monospace, monospace",
  sans: "'Geist', 'Helvetica Neue', Arial, sans-serif",
};

// Google's Fraunces subset defaults to wght 900 and WONK 1, so weight and WONK
// are always declared. Optical sizing is pinned per slot, never automatic, so a
// responsive font-size can't drag opsz with it. Same rule as the portfolio.
const fraunces = (opsz) => ({
  fontFamily: T.display,
  fontWeight: 400,
  fontOpticalSizing: "none",
  fontVariationSettings: `"opsz" ${opsz}, "SOFT" 30, "WONK" 0`,
});

const FONT_IMPORT = "@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=Geist+Mono:wght@400;500&family=Fraunces:ital,opsz,wght,SOFT,WONK@0,9..144,100..900,0..100,0..1;1,9..144,100..900,0..100,0..1&display=swap');";

const TYPE_SHAPE = { text: "○", image: "◆", audio: "△" };

// ink density by resonance — the only "colour" a user fragment gets
const inkDensity = (w) => `rgba(232,230,224,${(0.28 + w * 0.62).toFixed(2)})`;

function uid() { return Math.random().toString(36).slice(2, 8); }

// MOCK builds a reading over the fragments actually on the canvas, because a
// relation graph cannot be canned: its ids have to exist. Deterministic, so the
// preview build does not pretend to have model variation it does not have.
const mockReading = (frags) => {
  const read = frags.filter(f => f.type !== "image");
  const relations = [];
  for (let i = 0; i + 1 < read.length; i += 2) {
    relations.push({ a: read[i].id, b: read[i + 1].id, fit: i === 0 ? "strong" : "weak", label: "rough holds smooth" });
  }
  return {
    relations,
    unlinked: read.length % 2 ? [read[read.length - 1].id] : [],
    fragments: read.map(f => ({ id: f.id, words: ["heavy", "cold", "curved"] })),
  };
};

function analyzeAudio(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctx.decodeAudioData(e.target.result, (buf) => {
        const d = buf.getChannelData(0);
        const step = Math.floor(d.length / 64);
        const waveform = Array.from({ length: 64 }, (_, i) => { let max = 0; for (let j = 0; j < step; j++) { const v = Math.abs(d[i * step + j] || 0); if (v > max) max = v; } return max; });
        ctx.close();
        resolve({ waveform, amplitude: Math.max(...waveform), avgEnergy: waveform.reduce((a, b) => a + b, 0) / waveform.length, duration: buf.duration });
      }, () => resolve({ waveform: Array(64).fill(0.1), amplitude: 0.1, avgEnergy: 0.1, duration: 0 }));
    };
    reader.readAsArrayBuffer(file);
  });
}

function detectClusters(fragments, threshold = 160) {
  const clusters = []; const assigned = new Set();
  fragments.forEach((fa, i) => {
    if (assigned.has(fa.id)) return;
    const cluster = [fa]; assigned.add(fa.id);
    fragments.forEach((fb, j) => { if (i === j || assigned.has(fb.id)) return; if (Math.sqrt((fa.x - fb.x) ** 2 + (fa.y - fb.y) ** 2) < threshold) { cluster.push(fb); assigned.add(fb.id); } });
    if (cluster.length >= 2) clusters.push(cluster);
  });
  return clusters;
}

function GravityField({ fragments, hoveredId, rippleId, field }) {
  const res = (f) => feltResonance(f.x, f.y, field.cx, field.cy);
  if (fragments.length < 2) return null;
  const cx = fragments.reduce((s, f) => s + f.x, 0) / fragments.length;
  const cy = fragments.reduce((s, f) => s + f.y, 0) / fragments.length;
  const clusters = detectClusters(fragments);
  return (
    <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 3 }}>
      <defs>{fragments.map(f => (<radialGradient key={f.id} id={`grd-${f.id}`} cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor={f.color} stopOpacity="0.10" /><stop offset="100%" stopColor={f.color} stopOpacity="0" /></radialGradient>))}</defs>
      {clusters.map((cluster, ci) => { const minX = Math.min(...cluster.map(f => f.x)) - 52, maxX = Math.max(...cluster.map(f => f.x)) + 52; const minY = Math.min(...cluster.map(f => f.y)) - 44, maxY = Math.max(...cluster.map(f => f.y)) + 44; return <ellipse key={`cl-${ci}`} cx={(minX + maxX) / 2} cy={(minY + maxY) / 2} rx={(maxX - minX) / 2} ry={(maxY - minY) / 2} fill="none" stroke={cluster[0].color} strokeWidth={0.8} strokeOpacity={0.18} strokeDasharray="3 5" style={{ transition: "all 0.6s" }} />; })}
      {fragments.map(f => <ellipse key={`blob-${f.id}`} cx={f.x} cy={f.y} rx={30 + res(f) * 60} ry={26 + res(f) * 50} fill={`url(#grd-${f.id})`} opacity={hoveredId === f.id ? 1 : 0.55} style={{ transition: "opacity 0.3s" }} />)}
      {rippleId && fragments.filter(f => f.id === rippleId).map(f => [1, 2, 3].map(i => <circle key={`rip-${i}`} cx={f.x} cy={f.y} r={20 + i * 28 + res(f) * 30} fill="none" stroke={f.color} strokeWidth={0.6} strokeOpacity={0.12 / i} />))}
      {fragments.map((fa, i) => fragments.slice(i + 1).map(fb => { const hi = hoveredId === fa.id || hoveredId === fb.id; return <line key={`${fa.id}-${fb.id}`} x1={fa.x} y1={fa.y} x2={fb.x} y2={fb.y} stroke={hi ? fa.color : T.inkFaint} strokeWidth={hi ? 1 : 0.4} strokeOpacity={hi ? 0.4 : 0.16} strokeDasharray={hi ? "none" : "2 6"} style={{ transition: "all 0.3s" }} />; }))}
      {fragments.map(f => <line key={`c-${f.id}`} x1={f.x} y1={f.y} x2={cx} y2={cy} stroke={f.color} strokeWidth={0.5 + res(f) * 1.5} strokeOpacity={0.12 + res(f) * 0.18} strokeDasharray="2 6" style={{ transition: "all 0.3s" }} />)}
      <circle cx={cx} cy={cy} r={2} fill={T.inkMid} opacity={0.2} /><circle cx={cx} cy={cy} r={6} fill="none" stroke={T.inkMid} strokeWidth={0.5} opacity={0.1} strokeDasharray="2 3" />
    </svg>
  );
}

const FRAGMENT_CAP = 12;

// 2-1. Image fragments are not sent to the reading in this cut. The path is kept
// intact (upload, resize, base64, canvas placement) and simply not read, so the
// multimodal step can switch it back on. An unread image is NOT "unlinked" —
// unlinked means the model considered it and declined. It is marked "not read".
const READ_IMAGES = false;

// The languages a reading can be asked for. The cache lives in
// ./readingCache.js — the key is over the whole set (spec §0.9).
const READING_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ko", label: "한국어" },
  { code: "es", label: "Español" },
  { code: "ja", label: "日本語" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
];
const defaultReadingLanguage = () => {
  const tag = (navigator.language || "en").slice(0, 2).toLowerCase();
  return READING_LANGUAGES.some(l => l.code === tag) ? tag : "en";
};

// A drag gesture cannot tell 61 from 62, so the UI must not print either: five
// bands is what the hand can actually author. Display only — the value this
// coarsens is kept whole in the record, because a band is a poor thing to
// verify against (see docs, 3-3: display coarsens, verification goes to source).
// ── the research record ────────────────────────────────────────────
// 5-1 measures a failure rate, which needs both a numerator and a denominator.
// Until now only successes were written here, so the denominator was the only
// observable thing and the rate could not be computed at all.
//
// Every v6-era entry carries `status`. That is now the legacy test — entries
// written before v6 have neither `status` nor `protocolVersion`, and a failed
// attempt can legitimately have no protocolVersion (the server may never have
// answered), so protocolVersion alone would file a v6 failure as pre-v6 data.
// Phase 4's export splits on `status` being absent.
const LOG_KEY = "ic_log";
function writeLog(entry) {
  try {
    const L = JSON.parse(localStorage.getItem(LOG_KEY) || "[]");
    L.push(entry);
    localStorage.setItem(LOG_KEY, JSON.stringify(L));
  } catch { /* logging must never break a projection */ }
}

const RESONANCE_BANDS = 5;
const resonanceBand = (w) => Math.min(RESONANCE_BANDS, Math.max(1, Math.ceil(w * RESONANCE_BANDS)));

// feltResonance and the 3-1 freeze primitives live in ./freeze.js: they are the
// user's space, they are pure functions of coordinates, and 3-3 and Phase 4
// both read them. One definition, one place that must never see the model.

// Hand tracking has its own frame budget; 24fps is enough for a pointer and a pinch.
const DETECT_INTERVAL_MS = 1000 / 24;

// A pointer press is a selection until it has travelled this far. Below it, a press
// that wobbles must not be written back as a placement: position feeds resonance and
// bumps the fragment's move count, both of which are research record in v6.
const DRAG_THRESHOLD = 5;

// Two centres closer than a quarter of the widest card are covering each other
// rather than sitting near each other. The mark says so; it does not fix it.
// Nudging the cards apart, or floating the labels off them, would edit the
// arrangement to make it legible, and the arrangement is the measurement.
const STACK_RADIUS = 50;

const FragmentNode = memo(function FragmentNode({ frag, resonance, onDragEnd, onDragging, canvasRef, onHover, onDelete, isNaming, onName, onStartNaming, depth = 0, stacked = false }) {
  const [hovered, setHovered] = useState(false);
  const drag = useRef(null);
  const vibRef = useRef(null);
  const nodeRef = useRef(null);
  // Position and per-frame vibration live in refs and are written straight to the
  // element. React renders this node for structure only: text, naming, hover chrome.
  const rootRef = useRef(null);
  const posRef = useRef({ x: frag.x, y: frag.y });
  const vibORef = useRef({ x: 0, y: 0 });
  const paint = useCallback(() => {
    const el = rootRef.current; if (!el) return;
    const { x, y } = posRef.current, v = vibORef.current;
    el.style.transform = `translate3d(${x + v.x}px, ${y + v.y}px, 0) translate(-50%, -50%)`;
  }, []);
  const setDragVisual = useCallback((on) => { rootRef.current?.classList.toggle("is-dragging", on); }, []);
  const isUnnamed = frag.named === false;
  const [nameVal, setNameVal] = useState("");
  const nameInputRef = useRef(null);
  useEffect(() => { if (isNaming) { setNameVal(""); const t = setTimeout(() => nameInputRef.current?.focus(), 10); return () => clearTimeout(t); } }, [isNaming]);

  useLayoutEffect(() => { paint(); });
  useEffect(() => { posRef.current = { x: frag.x, y: frag.y }; paint(); }, [frag.x, frag.y, paint]);

  useEffect(() => {
    if (frag.type !== "audio" || !frag.audioData) return;
    const amp = frag.audioData.amplitude * 3.5;
    let t = 0;
    const tick = () => { t += 0.06; vibORef.current = { x: Math.sin(t * 2.1) * amp, y: Math.cos(t * 1.6) * amp * 0.5 }; paint(); vibRef.current = requestAnimationFrame(tick); };
    vibRef.current = requestAnimationFrame(tick);
    return () => { if (vibRef.current) cancelAnimationFrame(vibRef.current); vibRef.current = null; vibORef.current = { x: 0, y: 0 }; paint(); };
  }, [frag.type, frag.audioData, paint]);

  const onMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    const r = canvasRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
    drag.current = { ox: e.clientX - posRef.current.x - r.left, oy: e.clientY - posRef.current.y - r.top, sx: e.clientX, sy: e.clientY, moved: false };
    const move = (me) => {
      if (!drag.current) return;
      if (!drag.current.moved) {
        if (Math.hypot(me.clientX - drag.current.sx, me.clientY - drag.current.sy) < DRAG_THRESHOLD) return;
        drag.current.moved = true; setDragVisual(true); onDragging(frag.id);
      }
      const rr = canvasRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
      posRef.current = { x: me.clientX - drag.current.ox - rr.left, y: me.clientY - drag.current.oy - rr.top };
      paint();
    };
    const up = (ue) => {
      if (!drag.current) return;
      const moved = drag.current.moved;
      if (moved) {
        const rr = canvasRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
        posRef.current = { x: ue.clientX - drag.current.ox - rr.left, y: ue.clientY - drag.current.oy - rr.top };
        paint();
        drag.current = null; setDragVisual(false); onDragging(null); onDragEnd(frag.id, { ...posRef.current });
      } else { drag.current = null; }
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };

  const onTouchStart = (e) => {
    e.stopPropagation();
    const t = e.touches[0];
    const r = canvasRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
    drag.current = { ox: t.clientX - posRef.current.x - r.left, oy: t.clientY - posRef.current.y - r.top, sx: t.clientX, sy: t.clientY, moved: false };
    const move = (me) => {
      if (!drag.current) return;
      const tt = me.touches[0];
      if (!drag.current.moved) {
        if (Math.hypot(tt.clientX - drag.current.sx, tt.clientY - drag.current.sy) < DRAG_THRESHOLD) return;
        drag.current.moved = true; setDragVisual(true); onDragging(frag.id);
      }
      const rr = canvasRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
      posRef.current = { x: tt.clientX - drag.current.ox - rr.left, y: tt.clientY - drag.current.oy - rr.top };
      paint();
    };
    const up = (ue) => {
      if (!drag.current) return;
      const moved = drag.current.moved;
      if (moved) {
        const tt = ue.changedTouches[0];
        const rr = canvasRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
        posRef.current = { x: tt.clientX - drag.current.ox - rr.left, y: tt.clientY - drag.current.oy - rr.top };
        paint();
        drag.current = null; setDragVisual(false); onDragging(null); onDragEnd(frag.id, { ...posRef.current });
      } else { drag.current = null; }
      window.removeEventListener("touchmove", move); window.removeEventListener("touchend", up);
    };
    window.addEventListener("touchmove", move, { passive: false }); window.addEventListener("touchend", up);
  };

  const w = resonance, isImg = frag.type === "image", isAud = frag.type === "audio", isExtracted = frag.extracted;
  // ink density carries resonance; extracted fragments are the mirror's trace → rust
  const col = isExtracted ? T.mirror : inkDensity(w);
  const strong = w > 0.9;
  const size = Math.round(28 + w * 22);
  const fontSize = Math.round(11 + w * 2.5);
  const px = Math.round(10 + w * 8);
  const py = Math.round(6 + w * 4);
  return (
    <div ref={rootRef} className={`frag${isNaming ? " is-naming" : ""}`} onMouseDown={onMouseDown} onTouchStart={onTouchStart}
      onDoubleClick={() => onDelete(frag.id)}
      onMouseEnter={() => { setHovered(true); onHover(frag.id); }} onMouseLeave={() => { setHovered(false); onHover(null); }}
      style={{ userSelect: "none", "--frag-z": 10 + depth }}>
      {stacked && (
        /* Two dots, bottom-left, outside the body so no text reflows. It marks
           that something is underneath; it does not say what, and it does not
           move either card. */
        <div aria-hidden="true" style={{ position: "absolute", left: -9, bottom: -9, display: "flex", gap: 2, pointerEvents: "none" }}>
          <span style={{ width: 3, height: 3, borderRadius: "50%", background: T.inkLow }} />
          <span style={{ width: 3, height: 3, borderRadius: "50%", background: T.inkLow }} />
        </div>
      )}
      {hovered && (
        <div onMouseDown={e => { e.stopPropagation(); onDelete(frag.id); }}
          style={{ position: "absolute", top: -8, right: -8, width: 16, height: 16, borderRadius: "50%", background: T.bg2, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 30, fontSize: 9, color: T.inkLow, fontFamily: T.sans, lineHeight: 1 }}>×</div>
      )}
      <div style={{ position: "absolute", inset: -(8 + w * 20), borderRadius: "50%", border: `1px solid ${col}`, opacity: hovered ? 0.5 : 0.16, pointerEvents: "none", transition: "all 0.4s" }} />
      {w > 0.6 && <div style={{ position: "absolute", inset: -(18 + w * 30), borderRadius: "50%", border: `0.5px solid ${col}`, opacity: hovered ? 0.22 : 0.06, pointerEvents: "none" }} />}
      {isExtracted ? (
        <div className="frag-body" style={{ border: `1px dashed ${T.mirror}`, borderRadius: 10, padding: 3 }}>
          <div ref={nodeRef} style={{ background: hovered ? "rgba(196,136,90,0.08)" : T.bg3, borderRadius: 7, padding: isImg ? "2px" : `${py}px ${px}px`, display: "flex", alignItems: isImg ? "center" : "flex-start", gap: 7, whiteSpace: "normal", maxWidth: isImg ? "none" : 200 }}>
            <span style={{ fontSize: 8, color: T.mirror, opacity: 0.7, fontFamily: T.mono, flexShrink: 0, marginTop: isImg ? 0 : 3 }}>{TYPE_SHAPE[frag.type]}</span>
            {isImg && frag.imageUrl && <img src={frag.imageUrl} alt="" style={{ width: size, height: size, objectFit: "cover", borderRadius: 5 }} />}
            <span style={{ fontSize, color: T.mirror, fontFamily: T.mono, maxWidth: isImg ? 56 : 178, overflow: "hidden", lineHeight: 1.35, ...(isImg ? { whiteSpace: "nowrap", textOverflow: "ellipsis" } : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", wordBreak: "break-word" }) }}>{isImg && frag.text.length > 12 ? frag.text.slice(0, 12) + "…" : frag.text}</span>
          </div>
        </div>
      ) : (
        <div ref={nodeRef} className="frag-body" onClick={isUnnamed && !isNaming ? (e) => { e.stopPropagation(); onStartNaming(frag.id); } : undefined}
          style={{ background: hovered ? "rgba(232,230,224,0.06)" : "rgba(26,25,23,0.55)", border: `${strong ? 1.5 : 1}px ${isUnnamed && !isNaming ? "dashed" : "solid"} ${col}`, borderRadius: isImg ? 8 : (isAud ? 12 : 16), padding: isImg ? "4px" : `${py}px ${px}px`, display: "flex", alignItems: isImg || isAud ? "center" : "flex-start", gap: 7, boxShadow: strong ? "0 0 12px rgba(232,230,224,0.15)" : "none", backdropFilter: "blur(4px)", whiteSpace: "normal", maxWidth: isImg ? "none" : 200 }}>
          <span style={{ fontSize: 8, color: col, opacity: 0.7, fontFamily: T.mono, flexShrink: 0, marginTop: isImg || isAud ? 0 : 3 }}>{TYPE_SHAPE[frag.type]}</span>
          {isImg && frag.imageUrl && <img src={frag.imageUrl} alt="" style={{ width: size, height: size, objectFit: "cover", borderRadius: 5 }} />}
          {isAud && frag.audioData && (<svg width={60} height={20} viewBox="0 0 60 20">{frag.audioData.waveform.slice(0, 30).map((v, i) => { const max = Math.max(...frag.audioData.waveform, 0.01), h = Math.max(1.5, (v / max) * 18); return <rect key={i} x={i * 2} y={(20 - h) / 2} width={1.5} height={h} fill={col} opacity={0.4 + 0.6 * (v / max)} />; })}</svg>)}
          {isNaming ? (
            <input ref={nameInputRef} value={nameVal} onChange={e => setNameVal(e.target.value)} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } else if (e.key === "Escape") { setNameVal(""); e.currentTarget.blur(); } }}
              onBlur={() => onName(frag.id, nameVal)} placeholder="name it…"
              style={{ background: "transparent", border: "none", borderBottom: `1px solid ${T.border}`, color: T.ink, fontFamily: T.mono, fontSize, width: 116, padding: "0 0 2px" }} />
          ) : isUnnamed ? (
            <span style={{ fontSize, color: T.inkMid, fontFamily: T.mono, fontStyle: "italic", opacity: hovered ? 0.85 : 0, transition: "opacity 0.2s", whiteSpace: "nowrap" }}>name it…</span>
          ) : (
            <span style={{ fontSize, color: col, fontFamily: T.mono, maxWidth: isImg ? 56 : 178, overflow: "hidden", lineHeight: 1.35, ...(isImg ? { whiteSpace: "nowrap", textOverflow: "ellipsis" } : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", wordBreak: "break-word" }) }}>{isImg && frag.text.length > 12 ? frag.text.slice(0, 12) + "…" : frag.text}</span>
          )}
        </div>
      )}
      <div style={{ position: "absolute", bottom: -18, left: "50%", transform: "translateX(-50%)", fontSize: 10.5, color: hovered ? T.inkMid : T.inkLow, fontFamily: T.mono, whiteSpace: "nowrap", letterSpacing: "0.04em", opacity: hovered ? 1 : 0.6, transition: "all 0.2s", display: "flex", alignItems: "center", gap: 5 }}>
        {isImg && !READ_IMAGES && <span style={{ color: T.inkLow, borderBottom: `1px dotted ${T.inkFaint}` }} title="This image is placed on your canvas but is not sent to the reading in this version. Not read is not the same as unlinked.">not read</span>}
        <span>resonance</span>
        <span style={{ display: "flex", gap: 2 }}>
          {Array.from({ length: RESONANCE_BANDS }, (_, i) => (
            <span key={i} style={{ width: 6, height: 3, borderRadius: 1, background: i < resonanceBand(w) ? col : "transparent", border: i < resonanceBand(w) ? "none" : `1px solid ${T.inkFaint}`, boxSizing: "border-box" }} />
          ))}
        </span>
      </div>
    </div>
  );
});

function RecurringSignal({ words, centerX, centerY }) {
  const [visibleWords, setVisibleWords] = useState([]);
  useEffect(() => {
    setVisibleWords(prev => { const existing = new Set(prev.map(w => w.text)); const added = words.filter(w => !existing.has(w)); return [...prev, ...added.map(w => ({ text: w, born: Date.now() }))]; });
  }, [words.join(",")]);
  if (!visibleWords.length) return null;
  return (
    <div style={{ position: "absolute", left: centerX, top: centerY + 38, transform: "translateX(-50%)", pointerEvents: "none", userSelect: "none", maxWidth: 320, textAlign: "center", lineHeight: 1.8 }}>
      {visibleWords.map((w, i) => (<span key={w.text} style={{ ...fraunces(14), fontSize: 13, fontStyle: "italic", color: T.inkMid, letterSpacing: "0.02em", animation: "signalFadeIn 1.5s ease-in forwards" }}>{i > 0 && <span style={{ margin: "0 5px", opacity: 0.4 }}>·</span>}{w.text}</span>))}
    </div>
  );
}

function SessionLog({ events }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [events]);
  if (!events.length) return null;
  return (
    // `nowrap` + ellipsis cut the longest line the log can produce — the import
    // event, which carries the date that says WHICH record was opened
    // ("opened a record from 2…"). An elided date is the part that matters.
    //
    // Rows wrap now, but wrapping alone was not enough: a hyphen is a line-break
    // opportunity in CSS, so the line breaker took it greedily and split the
    // date into "2026-" / "08-19" — the same lost information, politer. Neither
    // overflow-wrap value prevents that, because it is normal line breaking
    // rather than overflow.
    //
    // So each WORD is its own nowrap span, and the separating space sits
    // OUTSIDE those spans. That placement is the whole mechanism: with the
    // space inside a nowrap span there is no break opportunity anywhere in the
    // row, and the line stopped wrapping altogether and overflowed sideways
    // instead — the date lost its last digit to a scroll edge rather than to an
    // ellipsis. Outside, breaks can only land between words, which makes any
    // hyphenated token — a date, an id, a fragment name — atomic without the
    // log having to know which is which.
    // Height is still capped and it still scrolls: a long line costs lines, not
    // information.
    <div ref={ref} style={{ position: "absolute", bottom: 64, left: 16, width: 244, maxHeight: 100, overflowY: "auto", background: `rgba(26,25,23,0.9)`, border: `1px solid ${T.border}`, borderRadius: 6, padding: "7px 11px", zIndex: 15 }}>
      {events.slice(-8).map((ev, i) => (<div key={i} style={{ fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, lineHeight: 1.9, display: "flex", gap: 6, alignItems: "baseline" }}><span style={{ color: T.inkLow, opacity: 0.6, flexShrink: 0 }}>{ev.time}</span><span style={{ color: ev.color || T.inkMid, minWidth: 0 }}>{String(ev.msg).split(" ").map((word, w) => (<Fragment key={w}>{w > 0 ? " " : null}<span style={{ whiteSpace: "nowrap" }}>{word}</span></Fragment>))}</span></div>))}
    </div>
  );
}


// 3-7. Waiting, without pretending to narrate.
//
// This replaces a four-step list on a 3.4s timer — "reading fragments /
// weighing resonance / finding patterns / weaving material" — which was wrong
// twice over. It was a scripted progress narrative advanced by a clock rather
// than by anything the request was doing (3-7 forbids exactly that), and
// "weighing resonance" described the one thing this instrument promises NOT to
// do: resonance is never sent (2-1). The interface was claiming, while the user
// waited, to be doing the thing the whole design exists to prevent.
//
// What is left is true: one call is out, nothing is known about its progress,
// and the two sentences from the single privacy source say what went with it.
// No trajectory, no pre-drawn edges — a shape that hinted at the result would
// be a reading before the reading.
function LoadingStages() {
  return (
    <div style={{ padding: "22px 28px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 14 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.mirror, animation: "pulse 2.4s ease-in-out infinite" }} />
        <span style={{ fontSize: 11.5, fontFamily: T.mono, letterSpacing: "0.06em", color: T.inkMid }}>waiting for the reading</span>
      </div>
      <div style={{ fontSize: 11.5, color: T.inkLow, fontFamily: T.sans, lineHeight: 1.65 }}>
        {PRIVACY.SENT} {PRIVACY.ARRANGEMENT}
      </div>
    </div>
  );
}

function EmptyMirror({ onSample }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: "0 32px", gap: 18 }}>
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" opacity={0.4}><circle cx="20" cy="20" r="16" stroke={T.inkFaint} strokeWidth="0.8" strokeDasharray="3 4" /><circle cx="20" cy="20" r="2.5" fill={T.inkFaint} />{[0, 72, 144, 216, 288].map((d, i) => <line key={i} x1={20 + Math.cos(d * Math.PI / 180) * 8} y1={20 + Math.sin(d * Math.PI / 180) * 8} x2={20 + Math.cos(d * Math.PI / 180) * 14} y2={20 + Math.sin(d * Math.PI / 180) * 14} stroke={T.inkFaint} strokeWidth="0.8" />)}</svg>
      <div style={{ textAlign: "center", fontSize: 13, color: T.inkMid, fontFamily: T.sans, lineHeight: 1.7 }}>drag fragments toward the center,<br />then freeze and read</div>
      <button onClick={onSample} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, fontFamily: T.mono, letterSpacing: "0.04em", color: T.inkMid, borderBottom: `1px solid ${T.border}`, paddingBottom: 2 }}
        onMouseEnter={e => { e.currentTarget.style.color = T.ink; }} onMouseLeave={e => { e.currentTarget.style.color = T.inkMid; }}>▸ see a sample reading</button>
    </div>
  );
}

// Failure spoken in the mirror's own grammar — no system error, no stack, just
// a clouded surface and a ghost retry that warms to rust on hover.
function CloudedMirror({ onRetry }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: "0 32px", gap: 20 }}>
      <div style={{ ...fraunces(18), textAlign: "center", fontSize: 18, color: T.inkMid, fontStyle: "italic", lineHeight: 1.6 }}>the reading did not come back.<br />try again in a moment.</div>
      <button onClick={onRetry} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, fontFamily: T.mono, letterSpacing: "0.06em", color: T.inkLow, borderBottom: `1px solid ${T.borderFaint}`, paddingBottom: 2, transition: "color 0.2s" }}
        onMouseEnter={e => { e.currentTarget.style.color = T.mirror; }} onMouseLeave={e => { e.currentTarget.style.color = T.inkLow; }}>retry →</button>
    </div>
  );
}

// "4th" reads as a position; "rank 4" reads as a score. The sentence template
// in 3-6 is written in ordinals, so the number is rendered as one.
const ordinal = (n) => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] || "th"}`;
};

function SectionLabel({ children }) {
  return <div style={{ fontSize: 11, color: T.inkLow, fontFamily: T.mono, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>{children}</div>;
}

// One grade above SectionLabel, for the one section that has sections under it.
// Three identical labels stacked read as three peers, and the comparison has to
// outrank the two lists that make it up. Same family, same case, same colour
// logic — brighter and spaced, not a new kind of thing.
function LeadLabel({ children }) {
  return (
    <div style={{ fontSize: 11.5, color: T.ink, fontFamily: T.mono, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 14 }}>
      {children}
    </div>
  );
}

// 3-3. Where the two spaces disagree.
//
// Every list states its FULL count and shows the rest behind an expander. The
// prototype capped each column at one item silently, which reads as "there was
// one" — the cap has to be visible or it is a claim about the data.
// `empty` and `none` are two different readings and a count of 0 arrives at
// both. `empty` means the measurement ran and found no divergence; `none` means
// there was nothing to measure. Printing `empty` for an empty denominator
// states an agreement that was never measured — with relations: [], "every
// relation the model drew was between fragments you had already placed nearest
// each other" is vacuously true and reads as the model agreeing with the user
// (5-3 item 5). Same rule as silence ≠ a weak link.
function DivergenceList({ label, count, of, empty, none, children }) {
  const [open, setOpen] = useState(false);
  const items = Array.isArray(children) ? children : [children];
  if (!count) return (
    <div style={{ marginBottom: 18 }}>
      <SectionLabel>{label}</SectionLabel>
      <div style={{ fontSize: 12.5, color: T.inkMid, fontFamily: T.sans, lineHeight: 1.6 }}>
        {of === 0 && none ? none : empty}
      </div>
    </div>
  );
  return (
    <div style={{ marginBottom: 18 }}>
      <SectionLabel>{label}</SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {(open ? items : items.slice(0, 1))}
      </div>
      {/* Collapsed, this said "1 of 2 · 3 considered · show all 2" — three
          numbers and a verb to say "there is one more". The remainder alone
          carries that, and the denominator comes back the moment the list is
          open, which is when it is actually being read.
          It does NOT disappear: when count is 1 there is nothing to expand, so
          the denominator is stated outright rather than hidden behind a control
          that would do nothing. A cap the reader cannot see is a claim about
          the data (the rule this component was built for). */}
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
        {count > 1 ? (
          <button onClick={() => setOpen(v => !v)}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 10.5, fontFamily: T.mono, color: T.inkMid, letterSpacing: "0.04em", borderBottom: `1px solid ${T.borderFaint}` }}>
            {open ? "collapse" : `+ ${count - 1} more`}
          </button>
        ) : null}
        {(open || count === 1) && of != null && (
          <span style={{ fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, letterSpacing: "0.04em" }}>
            {count} of {of} considered
          </span>
        )}
      </div>
    </div>
  );
}

// MODEL RELATIONS sits one step behind the comparison above it. Measured, not
// eyeballed: each value is 88% of the contrast of the tier it replaces, on bg2.
// The label's 5.35:1 is deliberately still above inkLow (4.56:1), because the
// theme's floor for TEXT is inkLow and a hierarchy change may not spend it.
const RELATION_INK = "#DBD9D3";          // was T.ink      14.08:1 → 12.45:1
const RELATION_INK_MID = "#908E86";      // was T.inkMid    6.08:1 →  5.35:1
const RELATION_BORDER = "rgba(232,230,224,0.12)";       // was 0.14
const RELATION_BORDER_FAINT = "rgba(232,230,224,0.07)"; // was 0.08

// A count that is always readable, over a list that is not always in the way.
// The count carries the claim (a refusal is a reading, 3-6) and stays on screen
// whether or not the row is open; only the names fold. Zero states its own
// sentence rather than offering an expander onto nothing.
function Diagnostic({ label, count, emptyNote, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ padding: "14px 28px", borderTop: `1px solid ${T.borderFaint}`, animation: "fadeUp 0.5s ease 0.16s both" }}>
      <button onClick={() => count > 0 && setOpen(v => !v)}
        disabled={count === 0}
        style={{ background: "none", border: "none", padding: 0, width: "100%", textAlign: "left", cursor: count > 0 ? "pointer" : "default", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 10.5, fontFamily: T.mono, letterSpacing: "0.12em", textTransform: "uppercase", color: T.inkLow }}>
          {label} · {count}
        </span>
        {count > 0 && (
          <span style={{ fontSize: 10, fontFamily: T.mono, color: T.inkLow }}>{open ? "−" : "+"}</span>
        )}
      </button>
      {count === 0 && emptyNote && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: T.inkLow, fontFamily: T.sans }}>{emptyNote}</div>
      )}
      {open && count > 0 && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  );
}

// 3-2. The relation graph, drawn where the client decided the nodes go.
// Rust throughout: this is the model's trace, and the rule is that colour
// appears only where the model is present (§8). The user's canvas stays ink.
//
// There is no centre mark and no axis here, on purpose. The user's space has a
// centre that means something; this one does not, and drawing one would invent
// a model-side counterpart to felt resonance that does not exist (§3-2).
const GRAPH_H = 210;
function ModelSpace({ reading, nameOf }) {
  const boxRef = useRef(null);
  const [w, setW] = useState(320);
  useEffect(() => {
    const el = boxRef.current; if (!el) return;
    const read = () => { const r = el.getBoundingClientRect(); if (r.width) setW(r.width); };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Laid out once per reading. The width only rescales the same picture —
  // recomputing the layout on resize would let the drawing change without the
  // reading changing, which is the thing 3-2 exists to rule out.
  const layout = useMemo(() => layoutReading(reading), [reading]);
  const placed = useMemo(() => toPixels(layout, { w, h: GRAPH_H, margin: 30 }), [layout, w]);
  const linked = new Set(layout.edges.flatMap(e => [e.a, e.b]));

  // Labels collide, and because the layout is deterministic they collide the
  // SAME way every time — "wet swimsuit" sat on "tapas" in every screenshot.
  // Resolved deterministically too: nodes are visited in id order and each
  // label takes the first offset whose box is free, so the picture stays a
  // function of the reading. Nothing here moves a NODE; only the text beside
  // it, which is already declared as drawn-for-legibility.
  const labelled = useMemo(() => {
    const OFFSETS = [-9, 16, -20, 27, -31];   // above, below, further out
    const taken = [];
    const hit = (b) => taken.some(t => !(b.x1 < t.x0 || b.x0 > t.x1 || b.y1 < t.y0 || b.y0 > t.y1));
    return [...placed.nodes]
      .sort((m, n) => (m.id < n.id ? -1 : m.id > n.id ? 1 : 0))
      .map(nd => {
        const label = (nameOf(nd.id) || "").slice(0, 14);
        const halfW = label.length * 2.9 + 2;   // ~5.8px per mono glyph at 10px
        let dy = OFFSETS[0];
        for (const o of OFFSETS) {
          const box = { x0: nd.x - halfW, x1: nd.x + halfW, y0: nd.y + o - 9, y1: nd.y + o + 2 };
          if (!hit(box)) { dy = o; taken.push(box); break; }
          if (o === OFFSETS[OFFSETS.length - 1]) { dy = o; taken.push(box); }
        }
        return { nd, label, dy };
      });
  }, [placed, nameOf]);

  return (
    <div ref={boxRef} style={{ position: "relative", height: GRAPH_H }}>
      <svg width={w} height={GRAPH_H} style={{ display: "block", overflow: "visible" }}>
        {placed.edges.map((e, i) => (
          <line key={`e-${i}`} x1={e.from.x} y1={e.from.y} x2={e.to.x} y2={e.to.y}
            stroke={T.mirror} strokeWidth={e.fit === "strong" ? 1.1 : 0.7}
            strokeOpacity={e.fit === "strong" ? 0.62 : 0.34}
            strokeDasharray={e.fit === "strong" ? "none" : "2 4"} />
        ))}
        {labelled.map(({ nd, label, dy }) => {
          // 3-6. filled = the model linked it, hollow = it did not.
          const isLinked = linked.has(nd.id);
          return (
            <g key={nd.id}>
              <circle cx={nd.x} cy={nd.y} r={4}
                fill={isLinked ? T.mirror : "none"}
                stroke={T.mirror} strokeWidth={isLinked ? 0 : 1} strokeOpacity={0.6}
                fillOpacity={0.85} />
              <text x={nd.x} y={nd.y + dy} textAnchor="middle" fontSize={10}
                fill={T.inkMid} fontFamily={T.mono}>
                {label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// 2-2 / 3-6. The reading is a relation graph, so the panel is a list of
// relations and a list of refusals — not three stacked layers. LEFT UNLINKED is
// a first-class section, at the same rank as the relations: the claim is that a
// refusal is a reading, and the layout has to say that too, not just the copy.
function OutputPanel({ pair, loading, mirrorError, gen, frags, onSample, frozenChanged, seeded }) {
  if (loading) return <LoadingStages />;
  if (mirrorError) return <CloudedMirror onRetry={onSample} />;
  if (!pair) return <EmptyMirror onSample={onSample} />;
  // The panel destructures the pair rather than taking two props: the reading
  // and its arrangement arrive together or not at all (3-3's signature).
  const { reading, snapshot } = pair;
  // 3-3. Measured from the pair, never from the reading alone — the signature
  // is the claim (see src/divergence.js).
  const div = snapshot ? divergence(reading, snapshot) : null;

  // 3-3b. Names come from the pair, not from the live canvas. The panel says the
  // arrangement is frozen, so the reading has to be able to say what the fragments
  // were called when it was asked for — deleting one afterwards used to rename it
  // "untitled" inside a snapshot that claims to be fixed.
  const byId = new Map(frags.map(f => [f.id, f]));
  const nameOf = (id) => pair.names?.[id] ?? (byId.get(id)?.text || "untitled");
  const relations = reading.relations || [];
  const unlinked = reading.unlinked || [];
  const strong = relations.filter(r => r.fit === "strong").length;
  const weak = relations.length - strong;
  const words = reading.fragments || [];

  return (
    <div style={{ height: "100%", overflowY: "auto", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "18px 28px 14px", borderBottom: `1px solid ${T.borderFaint}` }}>
        {/* 6.1.4-4. The header says what this panel IS, not only what it
            contains. It carries the snapshot id so the two sides can be
            compared at a glance: right after a freeze the canvas header shows
            the same six characters, and the moment a fragment moves only the
            canvas flips to EDITING. The line under this one keeps the freeze
            time and the prepared-example case; this is the state, that is the
            provenance. */}
        <SectionLabel>{snapshot
          ? `reading${gen > 0 ? ` ${String(gen).padStart(2, "0")}` : ""} — snapshot ${snapshot.id.slice(0, 6)}`
          : (gen > 0 ? `reading ${String(gen).padStart(2, "0")}` : "a reading")}</SectionLabel>
        {/* 3-6. the header count, so the shape of the graph is legible before
            reading it.
            The two frame sentences that used to sit under this block — "Not an
            answer. One relational reading." and the whole-set caveat — moved to
            `how it works` and to this hover. They were two lines of disclaimer
            standing between the reader and the result, which is half of what
            made the panel read as word-heavy. The claim is unchanged and still
            stated; it is just no longer the first thing read. */}
        <div style={{ fontSize: 11, color: T.inkMid, fontFamily: T.mono, letterSpacing: "0.06em", cursor: "help" }}
          title={"Not an answer. One relational reading.\n\nOf the whole set: adding or removing a fragment can change every relation. Measured, not assumed — the same two fragments came back strong in a ten-fragment set and weak in a six-fragment one."}>
          {strong} STRONG · {weak} WEAK · {unlinked.length} UNLINKED
        </div>
        {/* 3-1. Which arrangement this reading is held against. It says so even
            when nothing has moved, so the tie is legible before it is tested —
            the divergence in 3-3 is measured against this and never against
            whatever is on the canvas at the time of looking. */}
        {seeded && (
          <div style={{ marginTop: 8, fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, letterSpacing: "0.04em", lineHeight: 1.6 }}>
            a prepared example · yours as soon as you move anything
          </div>
        )}
        {snapshot && (
          <div style={{ marginTop: 8, fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, letterSpacing: "0.04em", lineHeight: 1.6 }}>
            {/* Named, not just referred to. The same reading can be held
                against more than one arrangement (2-4 keys the cache on
                content), so "which one" has to be on screen before 3-3 puts a
                number next to it. */}
            {/* "read from the arrangement above" was read as the model having
                read the arrangement — the exact inversion 2-1 exists to prevent.
                It meant "this reading is held against that arrangement", but the
                sentence cannot be told apart from "the model looked at it". Now
                the two sides are named in one line and neither can borrow the
                other's verb. */}
            <div style={{ color: T.inkMid }}>{snapshotLabel(snapshot)}</div>
            <div>{frozenChanged
              ? "human arrangement frozen above · the canvas has moved since"
              : "human arrangement frozen above · model read separate evidence"}</div>
          </div>
        )}
      </div>

      {/* ── B. the divergence, first ────────────────────────────────────
          This is the output. The two readings are each other's control, and
          the panel used to bury the comparison under the model's own list —
          which read as "here is what the AI found", the exact claim the
          instrument is built to refuse. It is one pair, one section, and it
          is what the first screen shows. */}
      {div && (
        <div style={{ padding: "22px 28px", borderBottom: `1px solid ${T.border}`, animation: "fadeUp 0.5s ease both" }}>
          <LeadLabel>where the readings diverged</LeadLabel>

          {/* Directly under the lead so it is on the first screen with the
              cards it qualifies — below them it would sit past the fold at the
              viewport this panel actually gets.
              "fragment text", not "descriptors": the descriptors are what the
              model WROTE. Naming them as the input would say the model was
              handed its own output, and the whole comparison rests on what went
              in. Verified against the intercepted body, not the spec. */}
          <div style={{ marginTop: -4, marginBottom: 14, fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, letterSpacing: "0.04em", lineHeight: 1.6 }}>
            model reading · generated from fragment text, not spatial position
          </div>

          {/* Symmetrical on purpose. The old header said the model "reached
              further than you did", which ranks the two readings and hands the
              model the higher seat — neither reading is the answer, so neither
              header may imply one. Both now name only who linked what. */}
          <DivergenceList
            label="the model linked what you didn't"
            count={div.modelSurprise.relations.filter(r => r.rank > 1).length}
            of={div.modelSurprise.total}
            empty="Every relation it drew was between fragments you had already placed nearest each other."
            none="No relation to rank against.">
            {div.modelSurprise.relations.filter(r => r.rank > 1).map((r, i) => (
              // The rank's denominator moved to the hover with the shorter
              // sentence. 5th-of-5 and 5th-of-11 are different claims, so the
              // scale cannot simply be dropped — but it does not belong in a
              // headline that was shortened on purpose.
              <div key={i} title={`${ordinal(r.rank)}-nearest of ${r.of} ranked in your arrangement`}
                style={{ padding: "10px 12px", border: `1px dashed ${T.borderFaint}`, borderRadius: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 5 }}>
                  <span style={{ fontSize: 12, color: T.ink, fontFamily: T.mono }}>{nameOf(r.a)}</span>
                  <span style={{ fontSize: 10, color: T.inkLow, fontFamily: T.mono }}>{r.fit === "strong" ? "———" : "- - -"}</span>
                  <span style={{ fontSize: 12, color: T.ink, fontFamily: T.mono }}>{nameOf(r.b)}</span>
                </div>
                {/* The rank stays in running text at body size. Setting it as
                    one big number would make the panel's loudest mark a
                    measure of how wrong the user was. */}
                <div style={{ fontSize: 12.5, color: T.inkMid, fontFamily: T.sans, lineHeight: 1.6 }}>
                  This was the {ordinal(r.rank)}-nearest relation in your arrangement.
                </div>
                {r.rankFromA !== r.rankFromB && (
                  <div style={{ marginTop: 4, fontSize: 10.5, color: T.inkLow, fontFamily: T.mono }}
                    title="A relation has no direction but a rank does. The sentence above uses whichever side read as nearer.">
                    {ordinal(r.rankFromA)} from {nameOf(r.a)} · {ordinal(r.rankFromB)} from {nameOf(r.b)}
                  </div>
                )}
              </div>
            ))}
          </DivergenceList>

          <DivergenceList
            label="you linked what the model didn't"
            count={div.unreadAdjacency.total}
            of={div.unreadAdjacency.ofNearestPairs}
            empty="Every nearest pair you made was also read as a strong relation."
            none="No adjacency to rank against.">
            {div.unreadAdjacency.pairs.map((p, i) => (
              <div key={i} style={{ padding: "10px 12px", border: `1px dashed ${T.borderFaint}`, borderRadius: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 5 }}>
                  <span style={{ fontSize: 12, color: T.ink, fontFamily: T.mono }}>{nameOf(p.a)}</span>
                  <span style={{ fontSize: 10, color: T.inkLow, fontFamily: T.mono }}>·</span>
                  <span style={{ fontSize: 12, color: T.ink, fontFamily: T.mono }}>{nameOf(p.b)}</span>
                </div>
                <div style={{ fontSize: 12.5, color: T.inkMid, fontFamily: T.sans, lineHeight: 1.6 }}>
                  {/* Silence and a weak link are different readings and must not
                      be printed as the same one. */}
                  {p.modelFit === null
                    ? "Their proximity did not survive the change of evidence."
                    : "You placed these nearest each other; the reading called the relation weak."}
                </div>
              </div>
            ))}
          </DivergenceList>

          {div.modelSurprise.unrankable > 0 && (
            <div style={{ fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, lineHeight: 1.6 }}>
              {div.modelSurprise.unrankable} relation{div.modelSurprise.unrankable > 1 ? "s" : ""} could not be
              placed in this arrangement, and {div.modelSurprise.unrankable > 1 ? "are" : "is"} left unmeasured.
            </div>
          )}
        </div>
      )}

      {/* ── C. the model's own list, second ─────────────────────────────
          Renamed from "relations": whose relations they are is the whole
          question, and an unqualified heading let them read as the relations. */}
      <div style={{ padding: "20px 28px", borderBottom: `1px solid ${T.borderFaint}`, animation: "fadeUp 0.5s ease 0.04s both" }}>
        {/* No READ AGAIN here on purpose. It is deferred to v6.1 with 3-4: a
            second reading without the self-variation measurement invites the
            user to read the difference as their own doing. */}
        <SectionLabel>model relations</SectionLabel>
        {relations.length === 0 ? (
          <div style={{ fontSize: 12.5, color: T.inkMid, fontFamily: T.sans, lineHeight: 1.6 }}>
            No relation was found. That is a reading, not a failure.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {relations.map((r, i) => (
              // Stepped back from the divergence cards above, not dimmed out:
              // nothing is removed and the strong/weak border still reads.
              // Both values are 88% of the tier they replace (T.ink 14.08:1 →
              // 12.45:1, T.inkMid 6.08:1 → 5.35:1 on bg2), which keeps the label
              // above inkLow's 4.56:1 AA floor — this is a hierarchy change, and
              // the theme's rule is that text never goes below that tier.
              <div key={i} style={{ padding: "10px 12px", border: `1px ${r.fit === "strong" ? "solid" : "dashed"} ${r.fit === "strong" ? RELATION_BORDER : RELATION_BORDER_FAINT}`, borderRadius: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 6 }}>
                  {/* 3-6. filled = the model linked it, hollow = it did not */}
                  <span style={{ fontSize: 9, color: T.mirror }}>●</span>
                  <span style={{ fontSize: 12, color: RELATION_INK, fontFamily: T.mono }}>{nameOf(r.a)}</span>
                  <span style={{ fontSize: 10, color: T.inkLow, fontFamily: T.mono }}>{r.fit === "strong" ? "———" : "- - -"}</span>
                  <span style={{ fontSize: 12, color: RELATION_INK, fontFamily: T.mono }}>{nameOf(r.b)}</span>
                </div>
                <div style={{ fontSize: 12.5, color: RELATION_INK_MID, fontFamily: T.sans }}>{r.label}</div>
                {r.demoted && (
                  <div style={{ marginTop: 5, fontSize: 10.5, color: T.inkLow, fontFamily: T.mono }} title="The model called this strong. It exceeded the cap of strong relations allowed on one fragment, so it is shown as weak.">
                    demoted to weak by the strong-edge cap
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── D. what the model had to go on ──────────────────────────────
          Split into read/wrote because the descriptors are the model's OUTPUT
          and the old heading ("what each one feels like") presented them as a
          property of the fragment — as if the instrument had sensed something.
          Naming the input on its own line is the load-bearing part: it is the
          one place the panel can say that the arrangement was never seen. */}
      {words.length > 0 && (
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${T.borderFaint}`, animation: "fadeUp 0.5s ease 0.08s both" }}>
          <SectionLabel>evidence the model read</SectionLabel>
          <div style={{ display: "flex", gap: 10, alignItems: "baseline", marginBottom: 10 }}>
            <span style={{ fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, minWidth: 44, letterSpacing: "0.04em" }}>read:</span>
            <span style={{ fontSize: 12.5, color: T.inkMid, fontFamily: T.sans }}>fragment text only</span>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
            <span style={{ fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, minWidth: 44, letterSpacing: "0.04em" }}>wrote:</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 7, minWidth: 0 }}>
              {words.map(w => (
                <div key={w.id} style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11.5, color: T.inkMid, fontFamily: T.mono }}>{nameOf(w.id)}</span>
                  <span style={{ fontSize: 10, color: T.inkLow, fontFamily: T.mono }}>→</span>
                  <span style={{ fontSize: 12.5, color: T.ink, fontFamily: T.sans }}>{(w.words || []).join(" · ")}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 12, fontSize: 11, color: T.inkLow, fontFamily: T.sans, lineHeight: 1.6 }}>
            The model did not see your arrangement. It read only the fragment text.
          </div>
        </div>
      )}

      {/* ── E. the picture, only when there is a shape to see ────────────
          3-2. Laid out deterministically from the ids, so re-reading cannot
          change the drawing on its own. Under two relations there is no shape:
          one edge or none is a diagram of a sentence already printed above,
          and it took the width of the panel to say nothing. */}
      {relations.length >= 2 && (
        <div style={{ padding: "18px 20px 12px", borderBottom: `1px solid ${T.borderFaint}`, animation: "fadeUp 0.5s ease 0.12s both" }}>
          <div style={{ padding: "0 8px" }}><SectionLabel>a model reading</SectionLabel></div>
          <ModelSpace reading={reading} nameOf={nameOf} />
          {/* Says what the drawing is, so nobody reads a distance out of it.
              Conditional with the drawing — a caption for an absent picture is
              a claim about nothing. */}
          <div style={{ padding: "10px 8px 0", fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, lineHeight: 1.6 }}>
            only the links are read here · positions are drawn for legibility
          </div>
        </div>
      )}

      {/* ── F. diagnostics ──────────────────────────────────────────────
          Still first-class as a claim — a refusal is a reading — but a count is
          enough to carry that, and the list itself was competing with the
          comparison for the same screen. The count is always visible; the names
          are one click away. */}
      <Diagnostic label="unlinked" count={unlinked.length}
        emptyNote="Nothing was left out.">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {unlinked.map(id => (
            <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, padding: "3px 10px", border: `1px dashed ${T.border}`, borderRadius: 100, color: T.inkMid, fontFamily: T.mono }}>
              <span style={{ fontSize: 9 }}>◯</span>{nameOf(id)}
            </span>
          ))}
        </div>
      </Diagnostic>
    </div>
  );
}

// ── hand tracking (logic unchanged) ─────────────────────────────
function countFingers(lm) {
  const tips = [8, 12, 16, 20];
  const pips = [6, 10, 14, 18];
  let count = 0;
  tips.forEach((tip, i) => { if (lm[tip].y < lm[pips[i]].y) count++; });
  if (Math.abs(lm[4].x - lm[2].x) > 0.06) count++;
  return count;
}

function isOpenHand(lm) {
  const pinching = Math.hypot(lm[4].x - lm[8].x, lm[4].y - lm[8].y) < 0.07;
  return countFingers(lm) >= 4 && !pinching;
}

function useHandTracking(canvasRef, onHandDrag, onGesture) {
  const videoRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const lastTime = useRef(-1);

  const posRef = useRef({ x: -1, y: -1 });
  const pinchRef = useRef(false);
  const activeRef = useRef(false);
  const grabRef = useRef(null);

  const fingerCountRef = useRef(0);
  const stillTimer = useRef(null);
  const lastPosSnapshot = useRef(null);
  const openHandTimer = useRef(null);
  const openHandStartRef = useRef(null);
  const gestureLockedRef = useRef(false);

  const [status, setStatus] = useState("off");

  const startCamera = useCallback(async () => {
    setStatus("loading");
    try {
      const { HandLandmarker, FilesetResolver } = await import("https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0");
      const vision = await FilesetResolver.forVisionTasks("https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm");
      landmarkerRef.current = await HandLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: "https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task", delegate: "GPU" },
        runningMode: "VIDEO", numHands: 1,
      });
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
      videoRef.current.srcObject = stream;
      videoRef.current.onloadeddata = () => setStatus("ready");
    } catch (e) {
      console.error("[hand]", e);
      setStatus("off");
    }
  }, []);

  useEffect(() => {
    if (status !== "ready" && status !== "tracking" && status !== "pinch") return;
    const video = videoRef.current;
    const overlay = overlayCanvasRef.current;

    // Detection runs on its own cadence, not once per rendered frame. rAF stays the
    // scheduler so the browser still parks the loop when the tab is hidden, but
    // inference is gated to ~24fps and never overlaps itself. The guard also means a
    // detect can never be in flight while stopCamera is tearing the stream down.
    let lastDetect = 0, inFlight = false;
    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      const now = performance.now();
      if (inFlight || now - lastDetect < DETECT_INTERVAL_MS) return;
      if (video?.readyState >= 2 && video.currentTime !== lastTime.current) {
        inFlight = true;
        lastDetect = now;
        lastTime.current = video.currentTime;
        try {
        const res = landmarkerRef.current?.detectForVideo(video, performance.now());

        if (res?.landmarks?.length > 0) {
          const lm = res.landmarks[0];
          const container = canvasRef.current;
          if (container) {
            const { width, height } = container.getBoundingClientRect();
            posRef.current = { x: (1 - lm[8].x) * width, y: lm[8].y * height };
          }

          const nowPinch = Math.hypot(lm[4].x - lm[8].x, lm[4].y - lm[8].y) < 0.055;
          const openHand = isOpenHand(lm);

          if (openHand && !grabRef.current && !gestureLockedRef.current) {
            if (!openHandTimer.current) {
              openHandStartRef.current = performance.now();
              openHandTimer.current = setTimeout(() => {
                gestureLockedRef.current = true;
                onGesture("open_hand");
                openHandTimer.current = null;
                openHandStartRef.current = null;
                setTimeout(() => { gestureLockedRef.current = false; }, 2000);
              }, 800);
            }
          } else {
            if (openHandTimer.current) {
              clearTimeout(openHandTimer.current);
              openHandTimer.current = null;
              openHandStartRef.current = null;
            }
          }

          if (!activeRef.current) { activeRef.current = true; setStatus("tracking"); }
          if (nowPinch !== pinchRef.current) {
            pinchRef.current = nowPinch;
            setStatus(nowPinch ? "pinch" : "tracking");
            if (!nowPinch && grabRef.current) {
              onHandDrag("end", grabRef.current.fragId, posRef.current);
              grabRef.current = null;
            }
          }

          if (nowPinch && grabRef.current) {
            onHandDrag("move", grabRef.current.fragId, posRef.current);
          }

          if (overlay) drawSkeleton(overlay, lm, nowPinch);
        } else {
          if (activeRef.current) { activeRef.current = false; setStatus("ready"); }
          if (pinchRef.current) { pinchRef.current = false; }
          if (grabRef.current) { onHandDrag("end", grabRef.current.fragId, posRef.current); grabRef.current = null; }
          if (openHandTimer.current) { clearTimeout(openHandTimer.current); openHandTimer.current = null; }
          if (overlay) overlay.getContext("2d").clearRect(0, 0, overlay.width, overlay.height);
        }
        } finally { inFlight = false; }
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); rafRef.current = null; };
  }, [status === "ready"]);

  const tryGrab = useCallback((fragments) => {
    if (!pinchRef.current || grabRef.current) return;
    const { x, y } = posRef.current;
    let closest = null, minDist = 70;
    fragments.forEach(f => {
      const d = Math.hypot(f.x - x, f.y - y);
      if (d < minDist) { minDist = d; closest = f; }
    });
    if (closest) {
      grabRef.current = { fragId: closest.id };
      onHandDrag("start", closest.id, { x, y });
    }
  }, []);

  const stopCamera = useCallback(() => {
    try { const s = videoRef.current?.srcObject; if (s) s.getTracks().forEach(t => t.stop()); if (videoRef.current) videoRef.current.srcObject = null; } catch { /* noop */ }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (openHandTimer.current) { clearTimeout(openHandTimer.current); openHandTimer.current = null; }
    activeRef.current = false; pinchRef.current = false; grabRef.current = null;
    try { const c = overlayCanvasRef.current?.getContext("2d"); if (c) c.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height); } catch { /* noop */ }
    setStatus("off");
  }, []);

  return { videoRef, overlayCanvasRef, posRef, pinchRef, activeRef, gestureLockedRef, openHandStartRef, status, startCamera, stopCamera, tryGrab };
}

function drawSkeleton(canvas, lm, pinching) {
  const W = canvas.width, H = canvas.height;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, W, H);
  const connections = [[0,1],[1,2],[2,3],[3,4],[0,5],[5,6],[6,7],[7,8],[0,9],[9,10],[10,11],[11,12],[0,13],[13,14],[14,15],[15,16],[0,17],[17,18],[18,19],[19,20],[5,9],[9,13],[13,17]];
  ctx.save(); ctx.scale(-1, 1); ctx.translate(-W, 0);
  const color = pinching ? "#C4885A" : "#9A9890";
  ctx.strokeStyle = color; ctx.lineWidth = 1; ctx.globalAlpha = 0.55;
  connections.forEach(([a, b]) => { ctx.beginPath(); ctx.moveTo(lm[a].x * W, lm[a].y * H); ctx.lineTo(lm[b].x * W, lm[b].y * H); ctx.stroke(); });
  lm.forEach((p, i) => { const isKey = i === 4 || i === 8; ctx.beginPath(); ctx.arc(p.x * W, p.y * H, isKey ? 4 : 2, 0, Math.PI * 2); ctx.fillStyle = isKey ? color : "rgba(154,152,144,0.5)"; ctx.globalAlpha = isKey ? 0.85 : 0.45; ctx.fill(); });
  ctx.restore(); ctx.globalAlpha = 1;
}

function HandCursorLayer({ posRef, pinchRef, activeRef, gestureLockedRef, openHandStartRef }) {
  const svgRef = useRef(null);
  const circleRef = useRef(null);
  const dotRef = useRef(null);
  const outerRef = useRef(null);
  const progressRef = useRef(null);
  const rafRef = useRef(null);

  const DURATION = 800;
  const RADIUS = 32;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  useEffect(() => {
    const tick = () => {
      if (svgRef.current && activeRef.current) {
        const { x, y } = posRef.current;
        const p = pinchRef.current;
        const locked = gestureLockedRef?.current || false;
        const startT = openHandStartRef?.current;

        svgRef.current.style.opacity = "1";
        // locked = the open-hand gesture has fired a projection → the mirror is about to act → rust
        const stateColor = locked ? T.mirror : p ? T.ink : T.inkFaint;

        if (circleRef.current) {
          circleRef.current.setAttribute("cx", x);
          circleRef.current.setAttribute("cy", y);
          circleRef.current.setAttribute("r", p ? 10 : locked ? 26 : 18);
          circleRef.current.setAttribute("stroke", stateColor);
          circleRef.current.setAttribute("stroke-opacity", locked ? "0.8" : "0.45");
        }
        if (dotRef.current) {
          dotRef.current.setAttribute("cx", x);
          dotRef.current.setAttribute("cy", y);
          dotRef.current.setAttribute("fill", stateColor);
        }
        if (outerRef.current) {
          outerRef.current.setAttribute("cx", x);
          outerRef.current.setAttribute("cy", y);
          outerRef.current.style.display = p ? "block" : "none";
        }

        if (progressRef.current) {
          if (startT && !locked) {
            const elapsed = performance.now() - startT;
            const pct = Math.min(elapsed / DURATION, 1);
            const offset = CIRCUMFERENCE * (1 - pct);
            progressRef.current.setAttribute("cx", x);
            progressRef.current.setAttribute("cy", y);
            progressRef.current.setAttribute("r", RADIUS);
            progressRef.current.setAttribute("stroke-dashoffset", offset);
            progressRef.current.style.display = "block";
          } else {
            progressRef.current.style.display = "none";
          }
        }
      } else if (svgRef.current) {
        svgRef.current.style.opacity = "0";
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <svg ref={svgRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 25, opacity: 0, transition: "opacity 0.3s" }}>
      <circle ref={progressRef}
        r={RADIUS} fill="none"
        stroke={T.mirror} strokeWidth={1.5} strokeOpacity={0.6}
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={CIRCUMFERENCE}
        strokeLinecap="round"
        transform={`rotate(-90)`}
        style={{ display: "none", transformOrigin: "center", transformBox: "fill-box" }}
      />
      <circle ref={circleRef} r={18} fill="none" strokeWidth={0.8} strokeOpacity={0.45} />
      <circle ref={dotRef} r={2.5} fillOpacity={0.6} />
      <circle ref={outerRef} r={28} fill="none" stroke={T.mirror} strokeWidth={0.5} strokeOpacity={0.25} strokeDasharray="2 4" style={{ display: "none" }} />
    </svg>
  );
}

// ── ghost marker — follows the cursor over empty canvas (replaces the input bar) ──
function GhostCursor({ canvasRef, active }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = canvasRef.current; if (!el) return;
    const move = (e) => { if (!ref.current) return; const r = el.getBoundingClientRect(); ref.current.style.transform = `translate(${e.clientX - r.left}px, ${e.clientY - r.top}px)`; ref.current.style.opacity = active && e.target === el ? "0.55" : "0"; };
    const leave = () => { if (ref.current) ref.current.style.opacity = "0"; };
    el.addEventListener("mousemove", move); el.addEventListener("mouseleave", leave);
    return () => { el.removeEventListener("mousemove", move); el.removeEventListener("mouseleave", leave); };
  }, [canvasRef, active]);
  return (
    <div ref={ref} style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", zIndex: 6, opacity: 0, transition: "opacity 0.2s" }}>
      <div style={{ position: "absolute", width: 22, height: 22, marginLeft: -11, marginTop: -11, borderRadius: "50%", border: `1px dashed ${T.inkFaint}` }} />
      <div style={{ position: "absolute", width: 2, height: 2, marginLeft: -1, marginTop: -1, borderRadius: "50%", background: T.inkFaint }} />
    </div>
  );
}

// ── 3-5. Orientation ───────────────────────────────────────────────
// Four panels, and each one declares a METHOD rather than describing a
// feature. "Click to place a fragment" is a tooltip; "your coordinates are
// kept, then withheld from the reading" is the thing the instrument is for.
//
// It only goes out with the functions it explains (Phase 2~3 in one deploy),
// for the same reason the input change and the claim change ship together: a
// modal that promises two spaces on a build that has one is a lie with a
// nicer typeface.
// One line each. A method declaration is SHORTER than a feature description,
// and the first draft managed to be both — 144 words of panel body, which made
// the modal taller than the viewport and pushed `begin` below the fold. The
// primary action being unreachable is the cost of over-explaining.
//
// It can be this short because the explaining is already done elsewhere: the
// seed is a worked example sitting behind this, and the interface states its
// own terms where they apply (the graph says only the links are read, the
// reading says what set it is of, a fragment says when it is not read).
// Fourth slot is an aside, set in italic under the step. Only 03 has one: the
// refusal claim is a property of the reading rather than another instruction,
// and folding it into 03's sentence was what made that sentence carry two ideas
// ("what it felt together" AND "refusing is a reading") in one breath.
//
// 03 also stopped saying "relation graph". The graph is one way the panel draws
// the reading and it is now conditional (it needs two relations), so naming the
// step after it described a picture that is sometimes not there. It names the
// model's reading, and says what that reading was made FROM — the same claim
// the panel makes, in the same words: fragment text.
const HOW_IT_WORKS = [
  ["01", "Place fragments", "Click anywhere. Position first; name it later, or never."],
  ["02", "Freeze the arrangement", "Your coordinates are saved, then withheld from the reading."],
  ["03", "See the model's reading",
    "See what the model related from the fragment text — and what it left unlinked.",
    "Leaving something unlinked is also a reading."],
  ["04", "Inspect the divergence", "Where the two disagree. Neither one is the answer."],
];

function IntroOverlay({ onClose, seeded }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(12,12,11,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "fadeIn 0.3s ease both", overflowY: "auto" }}>
      {/* Capped and split into three bands: the middle one scrolls, the action
          and the disclosure below it do not. Shortening the copy fixed today's
          overflow, but only a structure like this makes "begin is below the
          fold" impossible at any viewport height.
          The band still has a budget, and 03's aside spent the last of it: at a
          681px viewport, 04's sentence fell 91px below the fold, so the step
          that says "neither one is the answer" was only reachable by scrolling.
          Measured, not guessed — spacing was tightened until 04 cleared the
          fold. Content now needs a 705px viewport to sit entirely unscrolled;
          below that only the whole-set line (supplementary, and also stated in
          the panel's hover) goes under, and 01–04 stay together. */}
      <div style={{ maxWidth: 460, width: "100%", maxHeight: "calc(100vh - 24px)", background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "22px 30px 18px", margin: "auto", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div style={{ ...fraunces(24), fontSize: 22, color: T.ink, marginBottom: 9, flexShrink: 0 }}>Intuition Canvas</div>
        <div style={{ overflowY: "auto", minHeight: 0, flex: "0 1 auto", paddingBottom: 2 }}>
        <div style={{ fontFamily: T.sans, fontSize: 14, color: T.inkMid, lineHeight: 1.6, marginBottom: 11 }}>
          Two readings of the same fragments, from different evidence. You arrange them by feel; a model relates them by what they say.
        </div>
        {/* Moved here from the output panel, where it stood above the result and
            was read before there was anything to qualify. It belongs with the
            grammar, not in front of every reading. */}
        <div style={{ fontFamily: T.sans, fontSize: 13, color: T.inkLow, lineHeight: 1.6, marginBottom: 12, fontStyle: "italic" }}>
          Not an answer. One relational reading.
        </div>
        <div style={{ marginBottom: 11 }}>
          {HOW_IT_WORKS.map(([n, head, text, aside]) => (
            <div key={n} style={{ display: "flex", gap: 12, marginBottom: 8 }}>
              <span style={{ fontFamily: T.mono, fontSize: 11, color: T.inkLow, flexShrink: 0, marginTop: 2, letterSpacing: "0.04em" }}>{n}</span>
              <span>
                <span style={{ display: "block", fontFamily: T.mono, fontSize: 11.5, color: T.ink, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 }}>{head}</span>
                <span style={{ fontFamily: T.sans, fontSize: 13.5, color: T.inkMid, lineHeight: 1.55 }}>{text}</span>
                {/* Italic and dimmer: it is a claim about the reading, not a
                    further instruction, and it has to be legible as a different
                    kind of sentence from the one above it. */}
                {aside && (
                  <span style={{ display: "block", marginTop: 3, fontFamily: T.sans, fontSize: 13, color: T.inkLow, lineHeight: 1.55, fontStyle: "italic" }}>{aside}</span>
                )}
              </span>
            </div>
          ))}
          {/* Not a fifth step — a property of the instrument, so it sits apart
              from the numbered ones rather than swelling 04 into a paragraph.
              (§0.10, measured: the same two fragments came back strong in a
              ten-fragment set and weak in a six-fragment one.) */}
          <div style={{ marginTop: 10, paddingLeft: 23, fontFamily: T.sans, fontSize: 13, color: T.inkLow, lineHeight: 1.55 }}>
            A reading is of the whole set: add or remove one fragment and every relation can change.
          </div>
        </div>
        {/* The modal states the grammar; the seed shows a result of it. Saying
            so here is what keeps the opening canvas from reading as the
            visitor's own work. */}
        </div>
        {/* Outside the scrolling band, deliberately. This says what dismissing
            the modal drops you into, so it has to be legible at the moment the
            button is — inside the band its last line was sliced by the scroll
            edge and sat as a sliver against the button. */}
        {seeded && (
          <div style={{ flexShrink: 0, marginBottom: 14, fontFamily: T.sans, fontSize: 13, color: T.inkLow, lineHeight: 1.55 }}>
            A worked example is already on the canvas — yours as soon as you move anything.
            {/* The empty field was reachable only by knowing ?mode=open. Naming
                it here is disclosure, not a branch: the seeded canvas stays the
                default and study participants still arrive by the direct link. */}
            <a href="?mode=open" style={{ display: "block", marginTop: 7, fontFamily: T.mono, fontSize: 11.5, letterSpacing: "0.04em", color: T.inkMid, textDecoration: "none" }}
              onMouseEnter={e => { e.currentTarget.style.color = T.ink; }} onMouseLeave={e => { e.currentTarget.style.color = T.inkMid; }}>Start with an empty field →</a>
          </div>
        )}
        <button onClick={onClose} style={{ alignSelf: "flex-start", flexShrink: 0, background: T.ink, color: T.bg, border: "none", borderRadius: 6, padding: "9px 18px", fontFamily: T.mono, fontSize: 12, letterSpacing: "0.08em", cursor: "pointer", transition: "background 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "#F5F3ED"; }} onMouseLeave={e => { e.currentTarget.style.background = T.ink; }}>begin →</button>
        {/* 0-5: the pair that IS the claim, from the single source, as separate
            sentences — they are opposite facts and must never be merged.
            The other two are not dropped, they are stated where they apply and
            cannot be missed: CAMERA_LOCAL sits permanently beside the hand
            tracking control (0-3 requires it there), and an image fragment
            carries "not read" on its own label. Repeating them here was
            redundancy, not disclosure. */}
        <div style={{ flexShrink: 0, marginTop: 16, paddingTop: 13, borderTop: `1px solid ${T.borderFaint}`, fontFamily: T.sans, fontSize: 12, color: T.inkLow, lineHeight: 1.6 }}>
          {PRIVACY.SENT} {PRIVACY.ARRANGEMENT}
        </div>
        <div style={{ flexShrink: 0, marginTop: 11, fontFamily: T.mono, fontSize: 10.5, color: T.inkLow }}>Su Jin Park · ongoing research</div>
      </div>
    </div>
  );
}

// Phase 4. Export states what it is leaving out, and import says what it is
// about to overwrite. Both are one-way doors onto a research record, so
// neither happens on a bare click.
function RecordPanel({ log, onClose, onImport, sessionName, onSessionName }) {
  const [includeLegacy, setIncludeLegacy] = useState(true);
  const [error, setError] = useState(null);
  const [incoming, setIncoming] = useState(null);
  const fileRef = useRef(null);
  const preview = useMemo(() => buildExport(log, { includeLegacy, sessionName }), [log, includeLegacy, sessionName]);

  const download = () => {
    const record = buildExport(log, { includeLegacy, sessionName });
    const blob = new Blob([serializeExport(record)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = exportFilename(record);
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const readFile = (file) => {
    setError(null); setIncoming(null);
    const r = new FileReader();
    r.onload = () => {
      try { setIncoming(parseImport(String(r.result))); }
      catch (e) { setError(e.message); }
    };
    r.readAsText(file);
  };

  const row = { display: "flex", justifyContent: "space-between", fontSize: 11.5, fontFamily: T.mono, color: T.inkMid, padding: "3px 0" };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(12,12,11,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "fadeIn 0.3s ease both" }}>
      <div style={{ maxWidth: 470, width: "100%", maxHeight: "calc(100vh - 48px)", background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "28px 30px 22px", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div style={{ ...fraunces(20), fontSize: 19, color: T.ink, marginBottom: 6, flexShrink: 0 }}>The record</div>
        <div style={{ fontFamily: T.sans, fontSize: 13, color: T.inkMid, lineHeight: 1.6, marginBottom: 16, flexShrink: 0 }}>
          Every reading with the arrangement it was frozen against, so a reading can be reproduced rather than remembered.
        </div>

        <div style={{ overflowY: "auto", minHeight: 0 }}>
          <SectionLabel>what is in it</SectionLabel>
          <div style={row}><span>readings</span><span style={{ color: T.ink }}>{preview.counts.ok}</span></div>
          <div style={row}><span>from the cache</span><span style={{ color: T.ink }}>{preview.counts.cached}</span></div>
          <div style={row} title="Kept because a failure rate needs a numerator. Without these the record only shows what worked.">
            <span>attempts that failed</span><span style={{ color: T.ink }}>{preview.counts.failed}</span>
          </div>

          {/* The counts say how many readings. They do not say what a reading
              carries, and since 6.1 that includes when each fragment was made
              and when each one moved. Nothing here crosses the network, which
              is exactly why it needed saying somewhere else: SENT is about the
              request, and this file is handed to someone. */}
          <div style={{ marginTop: 12, fontFamily: T.sans, fontSize: 12, color: T.inkLow, lineHeight: 1.6 }}>
            {PRIVACY.RECORD} {PRIVACY.RECORD_IDENTITY}
          </div>

          {preview.counts.legacy > 0 && (
            <div style={{ marginTop: 16, padding: "12px 14px", border: `1px dashed ${T.border}`, borderRadius: 4 }}>
              <div style={{ ...row, paddingTop: 0 }}>
                <span>written before v6</span><span style={{ color: T.ink }}>{preview.counts.legacy}</span>
              </div>
              <div style={{ marginTop: 8, fontFamily: T.sans, fontSize: 12, color: T.inkLow, lineHeight: 1.6 }}>
                No protocol version, no frozen arrangement, and a stored `weight` from before resonance became derived. Not comparable with the readings above, and exported unmodified either way.
              </div>
              <label style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: T.mono, fontSize: 11, color: T.inkMid }}>
                <input type="checkbox" checked={includeLegacy} onChange={e => setIncludeLegacy(e.target.checked)} />
                include them
              </label>
              {!includeLegacy && (
                <div style={{ marginTop: 8, fontFamily: T.mono, fontSize: 10.5, color: T.inkLow, lineHeight: 1.6 }}>
                  {/* An omission that is not stated reads as an absence. */}
                  The file will still say {preview.counts.legacy} were left out.
                </div>
              )}
            </div>
          )}

          {incoming && (
            <div style={{ marginTop: 16, padding: "12px 14px", border: `1px solid ${T.border}`, borderRadius: 4 }}>
              <div style={{ fontFamily: T.mono, fontSize: 11, color: T.ink, marginBottom: 6 }}>
                {restorableRecords(incoming).length} reading{restorableRecords(incoming).length === 1 ? "" : "s"} to open
              </div>
              <div style={{ fontFamily: T.sans, fontSize: 12, color: T.inkLow, lineHeight: 1.6 }}>
                Recorded {(incoming.exportedAt || "").slice(0, 10)} under {incoming.instrument?.protocolVersion || "an unstated protocol"}.
                Opening the most recent one replaces what is on the canvas now.
              </div>
              <button onClick={() => { onImport(incoming); onClose(); }}
                style={{ marginTop: 10, background: T.ink, color: T.bg, border: "none", borderRadius: 5, padding: "7px 14px", fontFamily: T.mono, fontSize: 11.5, letterSpacing: "0.06em", cursor: "pointer" }}>
                open it →
              </button>
            </div>
          )}
          {error && (
            <div style={{ marginTop: 16, fontFamily: T.sans, fontSize: 12, color: T.mirror, lineHeight: 1.6 }}>{error}</div>
          )}
        </div>

        {/* 6.1-6. Asked here and nowhere else. A title offered before the
            arranging would be a frame to arrange inside — the whole instrument
            is about a person putting things down before they have said what
            they are doing. This modal is opened when the session is over, so
            the question cannot reach any arrangement it would have shaped.
            Optional on purpose: an empty answer is an answer. */}
        {preview.counts.ok + preview.counts.cached > 0 && (
          <div style={{ flexShrink: 0, marginTop: 18, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontFamily: T.sans, fontSize: 13, color: T.inkMid, marginBottom: 8 }}>
              What would you call this experience?
            </div>
            <input
              value={sessionName} onChange={e => onSessionName(e.target.value.slice(0, 80))}
              placeholder="optional"
              style={{ width: "100%", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 5, padding: "8px 10px", color: T.ink, fontFamily: T.mono, fontSize: 12, outline: "none" }}
            />
            <div style={{ marginTop: 6, fontFamily: T.mono, fontSize: 10.5, color: T.inkLow }}>
              goes in the file, not to the model
            </div>
          </div>
        )}

        <div style={{ flexShrink: 0, marginTop: 18, display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={download}
            style={{ background: T.ink, color: T.bg, border: "none", borderRadius: 5, padding: "8px 16px", fontFamily: T.mono, fontSize: 11.5, letterSpacing: "0.06em", cursor: "pointer" }}>
            export →
          </button>
          <button onClick={() => fileRef.current?.click()}
            style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 5, padding: "8px 14px", fontFamily: T.mono, fontSize: 11.5, color: T.inkMid, cursor: "pointer" }}>
            import…
          </button>
          <input ref={fileRef} type="file" accept="application/json,.json" style={{ display: "none" }}
            onChange={e => { const f = e.target.files?.[0]; if (f) readFile(f); e.target.value = ""; }} />
          <button onClick={onClose}
            style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontFamily: T.mono, fontSize: 11, color: T.inkLow }}>close</button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const canvasRef = useRef(null);

  // 3-5. The canvas opens seeded, not empty: six fragments and one frozen
  // reading, so the orientation's claims have something under them. Every
  // value was measured (docs/seed-selection.md), and the reading is baked
  // rather than fetched — a first screen that can fail to load is a first
  // screen that sometimes argues nothing.
  //
  // Placed against a provisional field; the ResizeObserver below corrects it to
  // the real canvas before paint, and the seed's frozen snapshot is taken from
  // that measurement rather than from these numbers.
  //
  // v6.1. `?mode=open` starts empty instead. The seeded canvas orients a first
  // visitor, but it also places six fragments the participant did not choose at
  // coordinates chosen to produce a known divergence — so a session run for
  // data has to be able to begin with nothing on it. Read once from the URL: a
  // start mode that could change mid-session would describe a session that
  // never happened.
  const [startMode] = useState(() =>
    new URLSearchParams(window.location.search).get("mode") === "open" ? "open" : "seeded");
  const [frags, setFrags] = useState(() =>
    startMode === "open" ? [] : seedFragments({ w: 720, h: 560 }).map(f => ({ ...f, color: T.ink })));
  const [namingId, setNamingId] = useState(null);
  const fragsRef = useRef(frags);
  useEffect(() => { fragsRef.current = frags; }, [frags]);

  // 3-1 / 3-3. A reading and the arrangement it was read from are ONE value,
  // never two pieces of state that happen to be updated together. 3-3's
  // signature is divergence(reading, snapshot), so the app must not be able to
  // hold a reading whose arrangement is unknown, or to update one without the
  // other. Set only where a reading is actually committed: a failed call leaves
  // the previous pair whole.
  const [pair, setPair] = useState(null);   // { reading, snapshot }
  const [loading, setLoading] = useState(false);
  const [mirrorError, setMirrorError] = useState(false);
  const [frozenToast, setFrozenToast] = useState(false);
  const [showRecord, setShowRecord] = useState(false);
  const [logSnapshot, setLogSnapshot] = useState([]);
  // 2-4. Versions come from the server so the cache key cannot drift from the
  // thing it identifies. Until they arrive the cache stays off and every read
  // goes to the model: a wrong hit is worse than a missed one.
  const [readingLanguage, setReadingLanguage] = useState(defaultReadingLanguage);
  const versionsRef = useRef(null);
  // The header chip reads from the same fetch, so the version on screen is the
  // protocol version the server actually reports rather than a literal typed
  // beside the title (src/version.js).
  const [protocolVersion, setProtocolVersion] = useState(null);
  useEffect(() => {
    let live = true;
    fetch("/api/mirror").then(r => r.json()).then(v => {
      if (live && v && v.protocolVersion && v.modelVersion) {
        versionsRef.current = v;
        setProtocolVersion(v.protocolVersion);
      }
    }).catch(() => {});
    return () => { live = false; };
  }, []);
  const [gen, setGen] = useState(0);
  // 2-4. The seed reading becomes the cache's first entry, so pressing
  // freeze & read on an untouched canvas returns it without a call. Written
  // under the versions it was MEASURED with — if the server now reports
  // different ones the key will not match and the model gets asked, which is
  // what a version bump is supposed to do.
  useEffect(() => {
    (async () => {
      const forReading = [...SEED_READING.fragments]
        .map(f => ({ id: f.id, text: seedFragments({ w: 1, h: 1 }).find(s => s.id === f.id)?.text || "" }));
      const key = await readingCacheKey(forReading, SEED_LENS, SEED_LANGUAGE, SEED_PROTOCOL_VERSION, SEED_MODEL_VERSION);
      if (!cacheRead(key)) {
        cacheWrite(key, { ts: new Date().toISOString(), reading: SEED_READING, language: SEED_LANGUAGE,
          languageDetected: null, languageMismatch: null, protocolVersion: SEED_PROTOCOL_VERSION,
          modelVersion: SEED_MODEL_VERSION, violations: null, seeded: true });
      }
    })().catch(() => { /* a cache that will not seed is a slow first read, not a broken one */ });
  }, []);
  const [dragOver, setDragOver] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [rippleId, setRippleId] = useState(null);
  const [sessionLog, setSessionLog] = useState([{ time: "00:00", msg: "session started", color: T.inkLow }]);
  const [recurringWords, setRecurringWords] = useState([]);
  // Automatic on the first visit only; the header keeps a way back in. A modal
  // that reappears every time stops being read after the second time.
  const [showIntro, setShowIntro] = useState(() => {
    // Never over a record. `?record=` means someone is being shown a reading
    // that already exists — on the table that is the moment the laptop turns
    // around, and an onboarding modal there would be the first thing the
    // participant sees instead of the panel.
    if (new URLSearchParams(window.location.search).get("record")) return false;
    try { return localStorage.getItem("ic_seen_intro") !== "1"; } catch { return true; }
  });
  const closeIntro = useCallback(() => {
    setShowIntro(false);
    try { localStorage.setItem("ic_seen_intro", "1"); } catch { /* private mode: shown again, not broken */ }
  }, []);

  // 6.1-7. The affordances that change how a session went, and only the ones
  // that exist: the orientation overlay, the camera, and opening a record onto
  // the canvas. Nothing is invented here — a flag for a helper this build does
  // not have would be a field that is always false and reads as a measurement.
  //
  // `orientation` is not a constant. ic_seen_intro means a returning visitor
  // does not get the overlay, so two sessions on the same laptop can differ,
  // which is exactly the sort of thing that would otherwise go unrecorded.
  // Persisted next to the log, and for the same reason: a reload mid-session
  // must not quietly drop something the visitor already wrote.
  const [sessionName, setSessionNameState] = useState(() => {
    try { return localStorage.getItem("ic_session_name") || ""; } catch { return ""; }
  });
  const setSessionName = useCallback((v) => {
    setSessionNameState(v);
    try { localStorage.setItem("ic_session_name", v); } catch { /* private mode: not persisted, not broken */ }
  }, []);

  const scaffolding = useRef({ orientation: false, handTracking: false, imported: false });
  useEffect(() => { if (showIntro) scaffolding.current.orientation = true; }, [showIntro]);
  const fragTextHistory = useRef([]);
  const patternHistory = useRef([]);

  const LENSES = ["PATTERN", "TENSION"];
  // One line each, and they have to say what the lens actually asks the model
  // for. src/readingRequest.js sends "Focus on recurring patterns across
  // fragments" and "Focus on conflicts, contrasts, and friction between
  // fragments"; api/mirror.js asks PATTERN to name the sensation shared across
  // ALL of them. So PATTERN is repetition, not grouping — on a canvas where
  // position is the language, "group" would read as the model sorting the
  // fragments by where they sit, which it never sees.
  const LENS_HINT = {
    PATTERN: "what repeats across them",
    TENSION: "where the words pull apart",
  };
  const [currentLens, setCurrentLens] = useState("PATTERN");
  const lensPathRef = useRef(["PATTERN"]);

  // The field centre is state, not a ref read, so resonance re-derives when the
  // canvas resizes. reweight() is deliberately gone: nothing rewrites a stored
  // weight any more, which is also why a drop no longer re-creates every
  // fragment object (see the render-count note in docs/phase-1-verification.md).
  // 1-3. Twelve is a ceiling, not a decay: the canvas never removes or fades a
  // fragment on its own. The user decides what leaves.
  const [capNotice, setCapNotice] = useState(false);
  const capNoticeTimer = useRef(null);
  const atCapacity = useCallback(() => {
    if (frags.length < FRAGMENT_CAP) return false;
    setCapNotice(true);
    if (capNoticeTimer.current) clearTimeout(capNoticeTimer.current);
    capNoticeTimer.current = setTimeout(() => setCapNotice(false), 4000);
    return true;
  }, [frags.length]);
  useEffect(() => () => { if (capNoticeTimer.current) clearTimeout(capNoticeTimer.current); }, []);

  // The field carries its size as well as its centre now: 3-1 normalises the
  // frozen snapshot by the field it was taken in, so the size is part of the
  // record and not something to be re-measured later from a resized window.
  // Declared before the field observer because the observer depends on it.
  const [isMobile, setIsMobile] = useState(window.innerWidth < 960);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 960); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);

  const [field, setField] = useState({ w: 720, h: 560, cx: 360, cy: 280 });
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const read = () => {
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const next = { w: r.width, h: r.height, cx: r.width / 2, cy: r.height / 2 };
      setField(prev => (prev.w === next.w && prev.h === next.h) ? prev : next);
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
    // isMobile, because the guard returns a different tree: crossing 960px and
    // back mounts a NEW canvas element, and an observer left on the old one
    // reports nothing ever again. The field then holds a stale size — and that
    // is not only a seed problem, because feltResonance is derived from
    // field.cx/cy, so every fragment would report a resonance measured against
    // a canvas that no longer exists.
  }, [isMobile]);
  const resonanceOf = useCallback((f) => feltResonance(f.x, f.y, field.cx, field.cy), [field]);

  // The seed is authored in normalised coordinates, so it becomes pixels only
  // once the canvas has been measured — and the FIRST measurement is the
  // provisional default above, not the real one. So this re-places on every
  // field change for as long as the seed is still the seed, which is what
  // keeps the demonstration correct at any window (spec §0.9).
  //
  // It stops on the one condition the panel already states out loud — "yours as
  // soon as you move anything". Reading the example does not make it yours, so
  // gen is deliberately NOT part of this: gating on it froze the seed in pixels
  // after a cached read, and shrinking the window then pushed a seed fragment
  // clean off the canvas while the notice still called it a prepared example.
  //
  // Re-freezing an unclaimed seed rewrites nothing: writeLog took its own copy
  // of the snapshot when the reading was committed, so the research record is
  // untouched and only the live pair is refreshed.
  //
  // Latching this on the first run was the earlier bug: the effect fired
  // against the 720x560 default, marked itself done, and the real measurement
  // never landed.
  // Whether the canvas is still the prepared example. One fact about the
  // session, not a field on each fragment: deriving it per-fragment is what let
  // the flag double as provenance and then be claimed away. It must NOT be
  // derived from `origin` — origin never changes, so the re-pin below would
  // stay alive for the whole session and quietly return every dragged seed
  // fragment to its seeded coordinates on the next resize.
  const [seedClaimed, setSeedClaimed] = useState(false);
  const seedIsLive = startMode === "seeded" && !seedClaimed && frags.length > 0;
  useEffect(() => {
    if (!seedIsLive || !field.w || !field.h) return;
    const placed = seedFragments(field).map(f => ({ ...f, color: T.ink }));
    setFrags(placed);
    // Marked at the one place the seed pair is built. Everything that names
    // this snapshot — the panel line, the canvas line, the footer — reads the
    // flag rather than deciding for itself, which is what kept them agreeing
    // about the id and disagreeing about everything else.
    // It rides along into the exported record too, which is correct: a restored
    // prepared example is still a prepared example.
    setPair({ reading: SEED_READING, snapshot: { ...freezeArrangement(placed, field), prepared: true }, names: Object.fromEntries(placed.map(f => [f.id, f.text])) });
  }, [seedIsLive, field]);
  // Freeze reads the element rather than the state so the snapshot cannot be
  // taken against a field measurement that a resize has not committed yet.
  const measureField = useCallback(() => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (r && r.width && r.height) return { w: r.width, h: r.height, cx: r.width / 2, cy: r.height / 2 };
    return field;
  }, [field]);

  // Recomputed each render: ≤12 fragments, and a memo here would only add a
  // second place for the frozen pair to go stale.
  const frozenChanged = arrangementChanged(pair?.snapshot, frags);
  // Which fragments are covering another one. Derived from the same x/y the
  // record keeps, recomputed when they move, and used for a mark only — it
  // never feeds the freeze, the payload or the divergence.
  const stackedIds = useMemo(() => {
    const out = new Set();
    for (let i = 0; i < frags.length; i++) {
      for (let j = i + 1; j < frags.length; j++) {
        if (Math.hypot(frags[i].x - frags[j].x, frags[i].y - frags[j].y) < STACK_RADIUS) {
          out.add(frags[i].id); out.add(frags[j].id);
        }
      }
    }
    return out;
  }, [frags]);
  // Nothing to read: a reading of this session exists (gen > 0, so not the
  // prepared example) and the arrangement under it has not moved since.
  const asleep = isAsleep({ gen, hasPair: !!pair, frozenChanged });
  useEffect(() => {
    if (!frozenChanged) { setFrozenToast(false); return; }
    setFrozenToast(true);
    const t = setTimeout(() => setFrozenToast(false), 6000);
    return () => clearTimeout(t);
  }, [frozenChanged]);

  // 3-5. The seed is on loan. The first time the visitor does anything to the
  // canvas the whole thing becomes theirs — not just the fragment they touched,
  // because a canvas half labelled "example" would be a canvas they cannot read
  // their own divergence off.
  const claimSeed = useCallback(() => setSeedClaimed(true), []);

  // Read at the moment the panel opens, not held in state as the session runs.
  // The log's home is localStorage; a second copy that drifts from it would be
  // a record of something that never happened.
  useEffect(() => {
    if (!showRecord) return;
    try { setLogSnapshot(JSON.parse(localStorage.getItem("ic_log") || "[]")); }
    catch { setLogSnapshot([]); }
  }, [showRecord]);

  // Phase 4 import. Puts the most recent restorable pair back on THIS canvas:
  // the snapshot is normalised, so the arrangement reproduces at any window and
  // the divergence figures come back identical (verify-phase-4 §7).
  const openRecord = useCallback((record) => {
    const restorable = restorableRecords(record);
    const entry = restorable[restorable.length - 1];
    if (!entry) return;
    const f = measureField();
    scaffolding.current.imported = true;
    // An opened record is not the prepared example, and the panel used to say
    // it was: seedIsLive only turns off when someone touches the canvas, and
    // importing is not touching the canvas. So the seed's caption — "a prepared
    // example · yours as soon as you move anything" — sat over somebody else's
    // frozen arrangement. Claim it here, where the canvas stops being the seed.
    claimSeed();
    setFrags(restoreFragments(entry, f).map(x => ({ ...x, color: T.ink })));
    setPair({ reading: entry.output, snapshot: entry.frozen, names: Object.fromEntries((entry.fragments || []).map(f => [f.id, f.text])) });
    setGen(g => Math.max(g, entry.gen || 0));
    setSessionLog(prev => [...prev, { time: "--:--", msg: `opened a record from ${(record.exportedAt || "").slice(0, 10)}`, color: T.mirror }]);
  }, [measureField, claimSeed]);

  // 6.1-9. `?record=/path.json` opens a record the way the import button does —
  // same parseImport, same openRecord, same panel.
  //
  // It exists for the table. A table session ends with the facilitator turning
  // the laptop around, and what the participant sees at that moment has to be
  // this panel and not a second rendering of it: the teach-back asks "what did
  // the model do" and "what does diverged mean", and if the two arms of the
  // Medium axis answer through different screens, a difference in the answers
  // cannot be read as a difference in medium.
  //
  // Same-origin only, by construction rather than by check — a bare fetch of a
  // relative path is subject to the origin the app was served from. On the
  // deployed build nothing serves records (.vercelignore, and the SPA fallback
  // returns index.html), so parseImport refuses and the canvas opens normally.
  const openedFromUrl = useRef(false);
  useEffect(() => {
    if (openedFromUrl.current) return;
    const src = new URLSearchParams(window.location.search).get("record");
    if (!src || /^[a-z]+:/i.test(src)) return;   // relative paths only
    openedFromUrl.current = true;
    fetch(src).then(r => r.text()).then(t => openRecord(parseImport(t)))
      .catch(e => setSessionLog(prev => [...prev, { time: "--:--", msg: `record not opened: ${e.message}`, color: T.mirror }]));
  }, [openRecord]);

  const logEvent = useCallback((msg, color) => {
    const now = new Date();
    // HH:MM:SS, local, the same clock snapshotLabel reads. It used to be
    // MM:SS, which made a log line at 16:22 sit under "frozen 21:16" and read
    // as a different moment when it was second 22 of that same minute. The
    // hour is what tells the reader the two agree. ISO in the record stays UTC.
    const time = [now.getHours(), now.getMinutes(), now.getSeconds()]
      .map(n => String(n).padStart(2, "0")).join(":");
    setSessionLog(prev => [...prev, { time, msg, color }]);
  }, []);

  // 6.1-4. Whether the visitor has seen a reading of their own yet. A ref
  // because only project() reads it, and because it must not be able to change
  // between the freeze and the record being written.
  //
  // The prepared example does NOT set this. It is a reading on the screen, but
  // it is not one the visitor asked for, and the footer already refuses to
  // count it — a session that opens seeded and is then arranged has not had a
  // reading of that arrangement.
  const readingSeen = useRef(false);
  // What the hand did since the last reading was committed. The rules are in
  // src/session.js so they can be checked.
  const edits = useRef(createEditLog());
  const noteEdit = useCallback((kind, id, extra) => edits.current.note(kind, id, extra), []);

  // A position in both forms. nx/ny IS the arrangement; x/y is where it lands
  // on this canvas. Everything that sets a position sets both through here, so
  // the two cannot drift into disagreeing about where a fragment is.
  const posFromPixels = useCallback((x, y) => {
    const f = measureField();
    return { nx: x / f.w, ny: y / f.h, x, y };
  }, [measureField]);

  // A new field makes the projection stale, so it is re-derived — not at render
  // time, because the drag path writes transforms straight to the DOM and reads
  // x/y back, and a fragment whose x said one thing while its nx said another
  // would be picked up at the wrong place on the next drag.
  //
  // Before v6.1 nothing did this. Fragments held pixels and kept them through a
  // resize, so the canvas grew around a fixed pattern of dots and every
  // normalised position moved with it: the same hand arrangement froze to
  // different coordinates depending on the window it was frozen in. The seed
  // re-pin was working around exactly this, for unclaimed seeds only.
  useEffect(() => {
    setFrags(prev => {
      let touched = false;
      const next = prev.map(f => {
        if (f.nx === undefined) return f;
        const x = f.nx * field.w, y = f.ny * field.h;
        if (x === f.x && y === f.y) return f;
        touched = true;
        return { ...f, x, y };
      });
      // Returning prev unchanged is what stops this from re-entering itself.
      return touched ? next : prev;
    });
  }, [field]);

  const handDragPos = useRef({});
  const onHandDrag = useCallback((type, fragId, pos) => {
    if (type === "start") {
      handDragPos.current[fragId] = pos;
      setRippleId(fragId);
    } else if (type === "move") {
      setFrags(prev => prev.map(f => f.id === fragId ? { ...f, ...posFromPixels(pos.x, pos.y) } : f));
    } else if (type === "end") {
      claimSeed();
      setFrags(prev => prev.map(f => {
        if (f.id !== fragId) return f;
        const to = posFromPixels(pos.x, pos.y);
        noteEdit("move", fragId, { from: { nx: f.nx, ny: f.ny }, to: { nx: to.nx, ny: to.ny }, by: "hand" });
        return { ...f, ...to };
      }));
      setRippleId(null);
      logEvent("hand drag ended", T.inkMid);
    }
  }, [logEvent, claimSeed, posFromPixels]);

  const onGestureRef = useRef(null);

  const { videoRef, overlayCanvasRef, posRef, pinchRef, activeRef, gestureLockedRef, openHandStartRef, status, startCamera, stopCamera, tryGrab } = useHandTracking(canvasRef, onHandDrag, useCallback((type, value) => { if (onGestureRef.current) onGestureRef.current(type, value); }, []));

  useEffect(() => {
    if (status === "off" || status === "loading") return;
    let prev = false;
    const id = setInterval(() => {
      const now = pinchRef.current;
      if (now && !prev) tryGrab(fragsRef.current);
      prev = now;
    }, 50);
    return () => clearInterval(id);
  }, [status, tryGrab]);

  const spawnPos = () => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const cx = rect ? rect.width / 2 : 360, cy = rect ? rect.height / 2 : 280;
    const maxR = 110, minR = 80;
    const a = Math.random() * Math.PI * 2, r = minR + Math.random() * (maxR - minR);
    return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r };
  };

  // Provenance for a fragment the visitor makes, decided once at creation and
  // never written again. The position is stored NORMALISED so it can be
  // compared with the frozen snapshot directly — an initial position in pixels
  // would have to be divided by whichever window it happened to be created in,
  // and that window is not recorded anywhere per fragment.
  const bornHere = useCallback((x, y) => {
    const f = measureField();
    return {
      origin: "user",
      initialPosition: { nx: x / f.w, ny: y / f.h },
      createdAt: new Date().toISOString(),
    };
  }, [measureField]);

  // place-first: a felt position, placed before it has a name
  const placeMarker = useCallback((x, y) => {
    if (atCapacity()) return;
    claimSeed();
    const id = uid();
    const born = posFromPixels(x, y);
    noteEdit("add", id, { to: { nx: born.nx, ny: born.ny }, type: "text" });
    setFrags(prev => [...prev, { id, text: "", type: "text", named: false, moves: 0, ...born, ...bornHere(x, y), color: T.ink }]);
    setNamingId(id);
    logEvent("point placed", T.inkMid);
  }, [logEvent, atCapacity, claimSeed, bornHere]);

  // names are born only on the canvas — commit or leave unnamed
  const nameMarker = useCallback((id, text) => {
    const t = (text || "").trim();
    setFrags(prev => prev.map(f => {
      if (f.id !== id) return f;
      if (f.text !== t) noteEdit("rename", id, { named: !!t });
      return { ...f, text: t, named: !!t };
    }));
    if (t) logEvent(`named: "${t.slice(0, 26)}"`, T.inkMid);
    setNamingId(null);
  }, [logEvent]);

  const compressImage = (file) => new Promise(res => {
    const url = URL.createObjectURL(file); const img = new Image();
    img.onload = () => { const MAX = 1024; let w = img.width, h = img.height; if (w > MAX || h > MAX) { if (w > h) { h = Math.round(h * MAX / w); w = MAX; } else { w = Math.round(w * MAX / h); h = MAX; } } const canvas = document.createElement("canvas"); canvas.width = w; canvas.height = h; canvas.getContext("2d").drawImage(img, 0, 0, w, h); const data = canvas.toDataURL("image/jpeg", 0.82).split(",")[1]; res({ url, base64: data, type: "image/jpeg" }); };
    img.src = url;
  });

  // Dropped files land where they were dropped. A drop is a placement — the same
  // gesture as a place-first click — so discarding the coordinate and spawning near
  // the centre loses the user's intent and stacks two files on each other.
  // `at` is optional: callers without a position keep the old scatter.
  const addImage = async (file, at) => {
    if (atCapacity()) return;
    const { url, base64, type } = await compressImage(file);
    // Resolved once: spawnPos() is random, so spreading it and then measuring
    // it again would record an initial position the fragment never had.
    const pos = at || spawnPos();
    const id = uid();
    const born = posFromPixels(pos.x, pos.y);
    noteEdit("add", id, { to: { nx: born.nx, ny: born.ny }, type: file.type.startsWith("audio/") ? "audio" : "image" });
    setFrags(prev => [...prev, { id, text: file.name.replace(/\.[^.]+$/, "").slice(0, 14), type: "image", imageUrl: url, imageBase64: base64, imageType: type, named: true, moves: 0, ...born, ...bornHere(pos.x, pos.y), color: T.ink }]);
    logEvent(`image: "${file.name.slice(0, 26)}"`, T.inkMid);
  };

  const addAudio = async (file, at) => {
    if (atCapacity()) return;
    const audioData = await analyzeAudio(file);
    const pos = at || spawnPos();
    const id = uid();
    const born = posFromPixels(pos.x, pos.y);
    noteEdit("add", id, { to: { nx: born.nx, ny: born.ny }, type: file.type.startsWith("audio/") ? "audio" : "image" });
    setFrags(prev => [...prev, { id, text: file.name.replace(/\.[^.]+$/, "").slice(0, 14), type: "audio", audioData, named: true, moves: 0, ...born, ...bornHere(pos.x, pos.y), color: T.ink }]);
    logEvent(`audio: "${file.name.slice(0, 26)}"`, T.inkMid);
  };

  const onDrop = useCallback(async (e) => {
    e.preventDefault(); setDragOver(false);
    // Same coordinate space as placeMarker's click: canvas-relative, not viewport.
    const rect = canvasRef.current?.getBoundingClientRect();
    const base = rect ? { x: e.clientX - rect.left, y: e.clientY - rect.top } : null;
    // Several files dropped at once cannot all occupy one point, so each after the
    // first steps along a short diagonal instead of stacking.
    let n = 0;
    for (const f of Array.from(e.dataTransfer.files)) {
      const at = base ? { x: base.x + n * 26, y: base.y + n * 22 } : null;
      if (f.type.startsWith("image/")) { await addImage(f, at); n++; }
      else if (f.type.startsWith("audio/")) { await addAudio(f, at); n++; }
    }
  }, [frags]);

  // Only the dropped fragment is re-created. Every other fragment keeps its
  // object identity, so a memoised FragmentNode can skip re-rendering it.
  const dragEnd = useCallback((id, pos) => {
    claimSeed();
    setFrags(prev => prev.map(f => {
      if (f.id !== id) return f;
      const to = posFromPixels(pos.x, pos.y);
      noteEdit("move", id, { from: { nx: f.nx, ny: f.ny }, to: { nx: to.nx, ny: to.ny } });
      return { ...f, ...to, moves: (f.moves || 0) + 1 };
    }));
    logEvent("fragment placed", T.inkFaint);
  }, [logEvent, claimSeed, posFromPixels]);

  const project = useCallback(async () => {
    if (!frags.length) return;
    setLoading(true);
    setMirrorError(false);
    // The counter only advances on a real projection. nextGen is committed inside
    // the success branch, so a failed call leaves gen (and the log) untouched.
    const nextGen = gen + 1;
    const currentTexts = frags.filter(f => f.type === "text").map(f => f.text);
    // 3-1. The arrangement is frozen at the moment the reading is asked for,
    // before any await, so a drag during the wait cannot end up in the record.
    // It is committed alongside the reading, never before: on a failure the
    // previous reading keeps the snapshot it already had.
    const snapshot = freezeArrangement(frags, measureField());
    // 6.1-4. Frozen with the arrangement and for the same reason: taken before
    // any await, so a drag during the wait belongs to the next reading rather
    // than to this one.
    //
    // `phase` is the state of the session at this freeze, not of this
    // arrangement. The record for a first reading says pre-reading even though
    // the reading is about to exist, which is right — the hand that made this
    // arrangement had not seen one. Session 01 needed exactly this: d957a0e3
    // frozen at 02:25:57 with nothing read yet, d20eb4c8 at 02:26:50 after.
    const phaseAtFreeze = phaseAt(readingSeen.current);
    const editsAtFreeze = edits.current.take();
    const editsWereTruncated = edits.current.truncated();
    // Every attempt shares this. The user's side of the record is the same
    // whether the model answered or not, and the resonances are derived from
    // the snapshot rather than the live field so a record opened on another
    // screen reports the values the user actually authored.
    const geo = frozenGeometry(snapshot);
    const frozenResonance = new Map(geo.points.map(p => [p.id, geo.resonanceAt(p)]));
    const frozenNames = Object.fromEntries(frags.map(f => [f.id, f.text]));
    const attemptRecord = () => ({
      ts: new Date().toISOString(),
      lens: currentLens,
      language: readingLanguage,
      // How the canvas opened. Session 01 could only be told apart from an
      // open-start session by recognising six hardcoded fragment ids; a record
      // has to say this about itself.
      start: startMode,
      // Whether a reading had been committed when this arrangement was frozen,
      // and what the hand did since the previous one. Together they answer the
      // question session 01 could only be answered by comparing timestamps
      // across two exports by hand: was this arrangement made before the
      // reading, or in response to it, and what exactly changed.
      phase: phaseAtFreeze,
      edits: editsAtFreeze,
      editsTruncated: editsWereTruncated,
      // Which helpers this session used. The boolean is a convenience over the
      // object, not a separate fact — a reader that wants to know which one
      // reads the object.
      scaffolding: { ...scaffolding.current },
      scaffoldingUsed: Object.values(scaffolding.current).some(Boolean),
      // 3-1. Normalised, with the field it was taken in. Phase 4 uses as-is.
      frozen: snapshot,
      // resonance is the user's side and was NOT sent: the reading never saw it.
      //
      // Provenance travels per fragment, all four fields additive and all four
      // absent from records written before v6.1 — a reader that finds them
      // missing knows the answer is unknown, not "no". `origin` says whether a
      // fragment was on the canvas before the visitor arrived, and
      // initialPosition + moves together say whether they ever touched it:
      // frozen == initial with moves 0 is a fragment left exactly where the
      // instrument put it. That question took a hand-built table to answer for
      // session 01.
      fragments: frags.map(f => ({
        id: f.id, text: f.text, type: f.type,
        resonance: frozenResonance.get(f.id) ?? null,
        sentToModel: READ_IMAGES || f.type !== "image",
        origin: f.origin ?? null,
        initialPosition: f.initialPosition ?? null,
        // Restored fragments come back with no move history — the count belongs
        // to the session that did the moving. null says unknown rather than
        // letting an import report a confident zero.
        moves: f.imported ? null : (f.moves ?? null),
        createdAt: f.createdAt ?? null,
      })),
    });
    // MOCK is deliberately not logged. It is a local preview with a canned
    // reading, and a fabricated entry in the research record is worse than a
    // missing one.
    if (MOCK) { await new Promise(r => setTimeout(r, 1300)); setGen(nextGen); fragTextHistory.current.push(currentTexts); setPair({ reading: mockReading(frags), snapshot, names: frozenNames }); readingSeen.current = true; edits.current.reset(); logEvent(`reading #${nextGen}`, T.mirror); logEvent("reading received", T.mirror); setLoading(false); return; }
    // 2-1. The reading is built from CONTENT ONLY. Nothing positional leaves the
    // browser: no coordinates, no resonance, no "near X", no move counts, and no
    // ordering derived from any of them. The arrangement is the user's evidence
    // and the reading must not be able to echo it back.
    //
    // Ordering is by fragment id, not by resonance: sorting by a spatial value
    // smuggles the arrangement into the prompt through the sequence itself.
    // src/readingRequest.js. Out of here so the table prototype can send the
    // same request rather than a second copy of it — the Medium axis compares
    // screen with table and only survives if the prompt is identical.
    const { forReading, content } = buildReadingContent(frags, currentLens, { readImages: READ_IMAGES });

    // 2-4. Same evidence, same reading. Moving fragments around and reading
    // again returns what is already stored, because none of that movement was
    // ever part of the request.
    const v = versionsRef.current;
    const key = v ? await readingCacheKey(forReading, currentLens, readingLanguage, v.protocolVersion, v.modelVersion) : null;
    if (key) {
      const hit = cacheRead(key);
      if (hit && isValidReading(hit.reading)) {
        setGen(nextGen);
        fragTextHistory.current.push(currentTexts);
        setPair({ reading: hit.reading, snapshot, names: frozenNames });
    // A reading of this arrangement now exists, so anything the hand does from
    // here is a response to it. The edit log restarts rather than accumulating:
    // each record carries what changed since the previous reading, not since
    // the session began.
    readingSeen.current = true;
    edits.current.reset();
        // The snapshot is NOT part of the cache entry, and must not become one.
        // The key is content, so the same stored reading can be asked for from
        // two different arrangements — one reading, two frozen snapshots. The
        // snapshot belongs to this reading of it, not to the cached text.
        logEvent(`reading #${nextGen}`, T.mirror);
        logEvent("same evidence, stored reading", T.inkMid);
        // Recorded as its own status. A cache hit is a reading the user saw but
        // NOT a call to the model, so it must not land in 5-1's denominator —
        // the failure rate is over {ok, failed}. It is still written, because
        // the reading/arrangement pair it produced is real and Phase 4 has to
        // be able to reproduce this screen.
        writeLog({
          ...attemptRecord(),
          status: "cached",
          gen: nextGen,
          protocolVersion: hit.protocolVersion || v?.protocolVersion || null,
          modelVersion: hit.modelVersion || v?.modelVersion || null,
          language: hit.language || readingLanguage,
          languageDetected: hit.languageDetected ?? null,
          languageMismatch: hit.languageMismatch ?? null,
          violations: hit.violations || null,
          output: hit.reading,
        });
        setLoading(false);
        return;
      }
    }
    // The reason is classified HERE, while the failures are still
    // distinguishable from each other. One catch-all at the bottom would
    // record every one of them as the same event, and 5-1 needs the modes
    // apart: congestion is the service, a schema mismatch is the protocol.
    let failure = null;
    let d = null;
    try {
      const res = await fetch("/api/mirror", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mode: "reading", language: readingLanguage, messages: [{ role: "user", content }] }) });
      try { d = await res.json(); } catch { d = null; }
      failure = classifyFailure(res.status, d, isValidReading);
    } catch {
      failure = { reason: "unreachable", httpStatus: null };
    }

    if (failure) {
      setMirrorError(true);
      logEvent(`reading failed: ${failure.reason}`, T.mirror);
      writeLog({
        ...attemptRecord(),
        status: "failed",
        reason: failure.reason,
        httpStatus: failure.httpStatus,
        detail: failure.detail ?? null,
        // gen numbers belong to committed readings only, and the counter does
        // not advance on failure — so the next success takes the number this
        // attempt was reaching for. Recording it as `gen` would put two
        // different things under one number.
        gen: null,
        attemptedGen: nextGen,
        // From the response when there is one, otherwise from the versions the
        // client already fetched. A failed attempt with no version at all would
        // be indistinguishable from a pre-v6 entry on export, which is why
        // `status` and not `protocolVersion` is the legacy test.
        protocolVersion: d?.protocolVersion || v?.protocolVersion || null,
        modelVersion: d?.modelVersion || v?.modelVersion || null,
        output: null,
      });
      setLoading(false);
      return;
    }

    const parsed = d.reading;
    // Success: now the projection counts.
    setGen(nextGen);
    fragTextHistory.current.push(currentTexts);
    setPair({ reading: parsed, snapshot, names: frozenNames });
    // A reading of this arrangement now exists, so anything the hand does from
    // here is a response to it. The edit log restarts rather than accumulating:
    // each record carries what changed since the previous reading, not since
    // the session began.
    readingSeen.current = true;
    edits.current.reset();
    logEvent(`reading #${nextGen}`, T.mirror);
    logEvent("reading received", T.mirror);
    if (key) cacheWrite(key, { ts: new Date().toISOString(), reading: parsed, language: d.language || readingLanguage, languageDetected: d.languageDetected ?? null, languageMismatch: d.languageMismatch ?? null, protocolVersion: d.protocolVersion || null, modelVersion: d.modelVersion || null, violations: d.violations || null });
    writeLog({
      ...attemptRecord(),
      status: "ok",
      gen: nextGen,
      protocolVersion: d.protocolVersion || null,
      modelVersion: d.modelVersion || null,
      // (c) the language a reading was asked for, what it came back in, and
      // whether those disagreed. null detected means undecidable, which is
      // recorded as null and never counted as agreement.
      language: d.language || readingLanguage,
      languageDetected: d.languageDetected ?? null,
      languageMismatch: d.languageMismatch ?? null,
      // 5-1. How much the raw response had to be corrected. Recorded per
      // reading so the rate is measurable, not remembered.
      violations: d.violations || null,
      output: parsed,
    });
    setLoading(false);
  }, [frags, gen, currentLens, measureField, readingLanguage]);

  useEffect(() => {
    onGestureRef.current = (type, value) => {
      if (type === "open_hand") {
        logEvent("hand: read", T.mirror);
        project();
      } else if (type === "lens") {
        const lensMap = { 1: "PATTERN", 2: "TENSION" };
        const next = lensMap[value];
        if (next) {
          setCurrentLens(prev => {
            if (next !== prev) {
              lensPathRef.current = [...lensPathRef.current, next];
              logEvent(`lens → ${next}`, T.inkMid);
            }
            return next;
          });
        }
      }
    };
  }, [project, logEvent]);

  useEffect(() => {
    if (gen < 3 || !pair) return;
    const phraseCounts = {};
    fragTextHistory.current.forEach(genTexts => { const seen = new Set(genTexts.map(t => t.toLowerCase().trim())); seen.forEach(phrase => { phraseCounts[phrase] = (phraseCounts[phrase] || 0) + 1; }); });
    setRecurringWords(Object.entries(phraseCounts).filter(([, c]) => c >= 2).sort(([, a], [, b]) => b - a).map(([p]) => p));
  }, [gen, pair]);

  useEffect(() => { const tags = (pair?.reading?.fragments || []).flatMap(f => f.words || []); if (tags.length) patternHistory.current.push(tags); }, [pair]);

  const deleteFragment = useCallback((id) => { claimSeed(); noteEdit("remove", id); setFrags(prev => prev.filter(f => f.id !== id)); logEvent("fragment removed", T.inkFaint); }, [logEvent, claimSeed, noteEdit]);
  const selectLens = (l) => {
    if (l === currentLens) return;
    setCurrentLens(l);
    lensPathRef.current = [...lensPathRef.current, l];
    logEvent(`lens → ${l}`, T.inkMid);
  };


  const statusLabel = { off: "hand tracking", loading: "loading…", ready: "scanning", tracking: "tracking", pinch: "pinch" };
  const statusColor = { off: T.inkFaint, loading: T.inkFaint, ready: T.inkMid, tracking: T.inkMid, pinch: T.ink };

  // ── mobile guard: desktop instrument ──────────────────────────
  if (isMobile) return (
    <div style={{ width: "100vw", height: "100vh", background: T.bg, color: T.ink, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: "0 34px", textAlign: "center" }}>
      <style>{FONT_IMPORT}</style>
      <svg width="22" height="22" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7.5" stroke={T.inkFaint} strokeWidth="0.7" /><circle cx="9" cy="9" r="1.8" fill={T.mirror} /><line x1="9" y1="1.5" x2="9" y2="5" stroke={T.inkFaint} strokeWidth="0.7" /><line x1="16.5" y1="9" x2="13" y2="9" stroke={T.inkFaint} strokeWidth="0.7" /><line x1="9" y1="16.5" x2="9" y2="13" stroke={T.inkFaint} strokeWidth="0.7" /><line x1="1.5" y1="9" x2="5" y2="9" stroke={T.inkFaint} strokeWidth="0.7" /></svg>
      <div style={{ ...fraunces(24), fontSize: 22, color: T.ink }}>Intuition Canvas</div>
      <div style={{ fontFamily: T.sans, fontSize: 14, color: T.inkMid, lineHeight: 1.6, maxWidth: "30ch" }}>Intuition Canvas is a desktop instrument. Best experienced with a pointer, on a larger screen.</div>
      <a href="https://sujinpark-portfolio.vercel.app" style={{ fontFamily: T.mono, fontSize: 10.5, color: T.inkLow, textDecoration: "none" }}>Su Jin Park · sujinpark-portfolio.vercel.app ↗</a>
    </div>
  );

  return (
    <div style={{ width: "100vw", height: "100vh", background: T.bg, display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: T.sans }}>
      <style>{`
        ${FONT_IMPORT}
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-thumb{background:${T.border}}
        input::placeholder{color:${T.inkLow}} input{outline:none}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes signalFadeIn{from{opacity:0}to{opacity:0.35}}
        @keyframes pulse{0%,100%{opacity:0.35}50%{opacity:1}}
        /* Fragments are placed with transform, never left/top, so a drag never
           touches layout and never needs React. .is-dragging is toggled directly
           on the element by the pointer handlers. */
        /* Base depth is placement order, carried in a custom property: the
           last fragment placed sits on top of the ones already there. It has
           to be a variable, not an inline z-index, or it would outrank the
           three rules below and a hovered card under a later one would stay
           buried. Nothing here moves a fragment — depth is the only thing
           that changes, because position is the language. */
        .frag{position:absolute;left:0;top:0;will-change:transform;cursor:grab;z-index:var(--frag-z,10)}
        .frag:hover,.frag.is-dragging,.frag.is-naming{z-index:9000}
        .frag.is-dragging{z-index:9001}
        .frag.is-dragging{cursor:grabbing}
        .frag-body{transform:scale(1);transition:all 0.2s cubic-bezier(0.34,1.56,0.64,1)}
        .frag:hover .frag-body{transform:scale(1.04)}
        .frag.is-dragging .frag-body{transform:scale(1.08)}
      `}</style>

      {showIntro && <IntroOverlay onClose={closeIntro} seeded={seedIsLive} />}
      {showRecord && (
        <RecordPanel log={logSnapshot} onClose={() => setShowRecord(false)} onImport={openRecord}
          sessionName={sessionName} onSessionName={setSessionName} />
      )}

      <header style={{ height: 52, padding: "0 22px", borderBottom: `1px solid ${T.borderFaint}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: T.bg, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7.5" stroke={T.inkFaint} strokeWidth="0.7" /><circle cx="9" cy="9" r="1.8" fill={T.inkMid} /><line x1="9" y1="1.5" x2="9" y2="5" stroke={T.inkFaint} strokeWidth="0.7" /><line x1="16.5" y1="9" x2="13" y2="9" stroke={T.inkFaint} strokeWidth="0.7" /><line x1="9" y1="16.5" x2="9" y2="13" stroke={T.inkFaint} strokeWidth="0.7" /><line x1="1.5" y1="9" x2="5" y2="9" stroke={T.inkFaint} strokeWidth="0.7" /></svg>
          <span style={{ ...fraunces(18), fontSize: 17, color: T.ink }}>Intuition Canvas</span>
          {displayVersion(protocolVersion) && (
            <span style={{ fontSize: 11, color: T.inkLow, fontFamily: T.mono }}
              title={`protocol ${protocolVersion}`}>{displayVersion(protocolVersion)}</span>
          )}
        </div>
        <div style={{ display: "flex", gap: 12, fontSize: 11, fontFamily: T.mono, color: T.inkLow, alignItems: "center" }}>
          {gen > 0 && <span style={{ color: T.ink }}>reading {String(gen).padStart(2, "0")}</span>}
          {/* 3-6. n/12, not a bare count. The ceiling is a rule of the
              instrument (1-3), so it should be legible before it is hit
              rather than announced only at the moment it blocks. */}
          <span style={{ color: frags.length >= FRAGMENT_CAP ? T.ink : T.inkLow }}
            title={`${FRAGMENT_CAP} fragments is the ceiling. Nothing is removed for you.`}>
            {frags.length}/{FRAGMENT_CAP} fragments
          </span>
          <span style={{ color: T.border }}>·</span>
          <span title="image fragments" style={{ color: frags.filter(f => f.type === "image").length > 0 ? T.ink : T.inkLow }}>{TYPE_SHAPE.image} {frags.filter(f => f.type === "image").length}</span>
          <span title="audio fragments" style={{ color: frags.filter(f => f.type === "audio").length > 0 ? T.ink : T.inkLow }}>{TYPE_SHAPE.audio} {frags.filter(f => f.type === "audio").length}</span>
          <button onClick={() => setShowIntro(true)} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: T.mono, fontSize: 11, color: T.inkLow, letterSpacing: "0.04em" }} onMouseEnter={e => e.currentTarget.style.color = T.ink} onMouseLeave={e => e.currentTarget.style.color = T.inkLow}>how it works</button>
          <a href="https://sujinpark-portfolio.vercel.app" target="_blank" rel="noreferrer" style={{ color: T.inkLow, textDecoration: "none", fontSize: 11 }} onMouseEnter={e => e.currentTarget.style.color = T.ink} onMouseLeave={e => e.currentTarget.style.color = T.inkLow}>Su Jin Park ↗</a>
          {MOCK && <span style={{ fontSize: 10.5, color: T.inkLow }}>preview</span>}
        </div>
      </header>

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <div ref={canvasRef} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
          onClick={(e) => { if (e.target === canvasRef.current) { const r = canvasRef.current.getBoundingClientRect(); placeMarker(e.clientX - r.left, e.clientY - r.top); } }}
          style={{ flex: 1, position: "relative", overflow: "hidden", background: T.bg, transition: "background 0.2s", cursor: "crosshair" }}>

          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", opacity: 1 }} viewBox="0 0 900 700" preserveAspectRatio="xMidYMid slice">
            {Array.from({ length: 30 }, (_, i) => <line key={`v${i}`} x1={i * 30} y1={0} x2={i * 30} y2={700} stroke={T.borderFaint} strokeWidth={1} />)}
            {Array.from({ length: 24 }, (_, i) => <line key={`h${i}`} x1={0} y1={i * 30} x2={900} y2={i * 30} stroke={T.borderFaint} strokeWidth={1} />)}
          </svg>
          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1 }}>
            {/* One step up in weight, not a redesign. The centre is the axis
                every resonance number is measured from, and at the old values
                it sank into the grid — the reference read as decoration. About
                15%: still quieter than any fragment. */}
            <defs><radialGradient id="centerGlow" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="rgba(232,230,224,0.058)" /><stop offset="100%" stopColor="rgba(232,230,224,0)" /></radialGradient></defs>
            <circle cx="50%" cy="50%" r={90} fill="url(#centerGlow)" />
            {[70, 140, 210, 280, 350].map((r, i) => <circle key={`ring-${i}`} cx="50%" cy="50%" r={r} fill="none" stroke={T.inkFaint} strokeWidth={1} strokeOpacity={0.32 - i * 0.05} strokeDasharray="3 7" />)}
            <circle cx="50%" cy="50%" r={5} fill={T.inkMid} fillOpacity={0.58} /><circle cx="50%" cy="50%" r={2.5} fill={T.ink} fillOpacity={0.92} />
            {/* 3-6. The centre says what nearness to it MEANS, not what the
                dot is called. "intuition center" named a point; "more felt"
                names the axis the user is actually authoring along.
                This label belongs to the user's space only — the model space
                has no centre and must not be given one (spec §3-2).
                Outside the innermost ring rather than under the dot: a fragment
                dropped on the centre sat on top of it and left "MORE F" on
                screen. ABOVE it, not below — below the centre is RecurringSignal's
                band (it starts at centreY + 38 and grows downward), and the first
                attempt at this moved the label straight into those words. Fixed
                offset either way: the label must not move with the canvas or it
                stops being a reference mark. */}
            <text x="50%" y="50%" dy={-80} textAnchor="middle" fontSize={10.5} fill={T.inkLow} fontFamily={T.mono} letterSpacing="0.12em">MORE FELT</text>
          </svg>

          {dragOver && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: T.inkMid, fontFamily: T.mono, pointerEvents: "none" }}>drop an image or audio file here</div>}

          {/* lens toggle — now clickable (segmented control) */}
          <div style={{ position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
            <div style={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: 5, overflow: "hidden" }}>
              {LENSES.map((l, i) => (
                <button key={l} onClick={() => selectLens(l)} title={LENS_HINT[l]}
                  style={{ fontSize: 11, fontFamily: T.mono, letterSpacing: "0.1em", padding: "5px 12px", background: currentLens === l ? T.ink : "transparent", color: currentLens === l ? T.bg : T.inkLow, border: "none", borderLeft: i > 0 ? `1px solid ${T.border}` : "none", cursor: "pointer", transition: "all 0.2s" }}
                  onMouseEnter={e => { if (currentLens !== l) e.currentTarget.style.color = T.inkMid; }} onMouseLeave={e => { if (currentLens !== l) e.currentTarget.style.color = T.inkLow; }}>{l}</button>
              ))}
            </div>
            {lensPathRef.current.length > 1 && (
              <div style={{ fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, letterSpacing: "0.04em", opacity: 0.6 }}>{lensPathRef.current.slice(-5).join(" → ")}</div>
            )}
          </div>

          <GhostCursor canvasRef={canvasRef} active={namingId === null && hoveredId === null} />
          <GravityField fragments={frags} hoveredId={hoveredId} rippleId={rippleId} field={field} />
          {(() => { const cw = canvasRef.current?.offsetWidth || 600; const ch = canvasRef.current?.offsetHeight || 400; return <RecurringSignal words={recurringWords} centerX={cw / 2} centerY={ch / 2} />; })()}

          <HandCursorLayer posRef={posRef} pinchRef={pinchRef} activeRef={activeRef} gestureLockedRef={gestureLockedRef} openHandStartRef={openHandStartRef} />

          {/* Index is placement order, so depth is placement order. Reading it
              from the array keeps the two the same thing rather than a second
              record that could drift from it. */}
          {frags.map((f, i) => <FragmentNode key={f.id} frag={f} resonance={resonanceOf(f)} onDragEnd={dragEnd} onDragging={setRippleId} canvasRef={canvasRef} onHover={setHoveredId} onDelete={deleteFragment} isNaming={namingId === f.id} onName={nameMarker} onStartNaming={setNamingId} depth={i} stacked={stackedIds.has(f.id)} />)}

          <div style={{ position: "absolute", top: 16, left: 18, maxWidth: 420, pointerEvents: "none" }}>
            {/* 6.1.4-4. Canvas state, above the instruction: where you are, then
                what to do. It sits inside this block rather than floating,
                because both other corners of the canvas are taken — the lens
                toggle centre, the hand-tracking control and its caption right —
                and an absolute label put there covered them.
                With a snapshot nothing has moved since, this shows the same six
                characters as the reading panel's header, so the pair is visibly
                tied. One move and only this side returns to EDITING: the reading
                keeps the arrangement it was actually read against, which is what
                freezing is for. The panel's "canvas has moved since" line stays —
                that says what changed, this says where you are. */}
            <div style={{ fontFamily: T.mono, fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: T.inkLow, marginBottom: 7 }}>
              {pair?.snapshot && !frozenChanged
                ? `canvas — snapshot ${pair.snapshot.id.slice(0, 6)}`
                : "canvas — editing · new arrangement"}
            </div>
            {/* Two grades, not three sentences. The first line is the only
                instruction — set at label grade so it reads as one, where the
                italic serif read as a caption on a picture and competed with
                the title. */}
            <div style={{ fontSize: 11.5, fontFamily: T.mono, color: T.ink, letterSpacing: "0.12em", textTransform: "uppercase" }}>
              click anywhere to place a fragment
            </div>
            {/* 3-6. The standing two-line legend. It replaces the old hint,
                which mixed a legend ("proximity is resonance") with a list of
                gestures. These two lines say what the two distances MEAN, and
                they stay on screen because the whole of 3-3 is measured
                against them — a reader who has to remember which distance is
                which cannot read their own divergence.
                The third line is gone. It offered "drop an image / audio file"
                beside the first instruction, and in this cut images are placed
                but never read (PRIVACY.IMAGES_LOCAL) and audio is not
                implemented — an invitation the instrument does not honour. */}
            <div style={{ marginTop: 7, fontSize: 12, fontFamily: T.mono, color: T.inkLow, lineHeight: 1.7 }}>
              near the center · more felt<br />near each other · felt adjacency
            </div>
            {/* What the canvas is holding, said on the canvas. Freezing does
                not disable anything — every fragment still moves — so the only
                way to tell a frozen canvas from an unread one was a line in the
                other column. Named with the same snapshotLabel the panel uses,
                so the two can never disagree about which arrangement it is. */}
            {pair?.snapshot && (
              <div style={{ marginTop: 10, display: "inline-block", background: "rgba(26,25,23,0.9)", border: `1px solid ${T.borderFaint}`, borderRadius: 6, padding: "7px 11px", fontSize: 11, fontFamily: T.mono, color: T.inkLow, lineHeight: 1.7 }}>
                <div style={{ color: T.inkMid }}>{snapshotLabel(pair.snapshot)}</div>
                {/* Same misreading as the panel's line, same fix: "read from
                    this" put the model's eyes on the canvas. */}
                <div>{frozenChanged
                  ? "frozen · the canvas moved since"
                  : "frozen · read separately"}</div>
              </div>
            )}
            {/* On its own ground, like the session log. This block sits over the
                canvas, and at twelve fragments there is a fragment under almost
                every part of it — the notice was landing behind one and reading
                as "Canvas full at 12. Remo… room." A refusal the user cannot
                read is a refusal that did not happen. */}
            {capNotice && (
              <div style={{ marginTop: 10, display: "inline-block", background: "rgba(26,25,23,0.9)", border: `1px solid ${T.border}`, borderRadius: 6, padding: "7px 11px", fontSize: 12, fontFamily: T.mono, color: T.mirror, letterSpacing: "0.02em" }}>
                Canvas full at {FRAGMENT_CAP}. Remove one to make room.
              </div>
            )}
          </div>

          <SessionLog events={sessionLog} />

          {/* hand tracking button + PiP */}
          <div style={{ position: "absolute", top: 16, right: 16, zIndex: 30, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: statusColor[status], transition: "background 0.3s" }} />
              {status === "off" || status === "loading" ? (
                <button onClick={() => { scaffolding.current.handTracking = true; startCamera(); }} disabled={status === "loading"} title="control modes with hand gestures (webcam)"
                  style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 12px", fontFamily: T.mono, fontSize: 11, letterSpacing: "0.04em", color: T.inkMid, cursor: status === "loading" ? "default" : "pointer" }}
                  onMouseEnter={e => { if (status !== "loading") e.currentTarget.style.color = T.ink; }} onMouseLeave={e => { e.currentTarget.style.color = T.inkMid; }}>
                  {statusLabel[status]}
                </button>
              ) : (
                <>
                  <span style={{ fontSize: 11, fontFamily: T.mono, color: statusColor[status], letterSpacing: "0.04em" }}>{statusLabel[status]}</span>
                  <button onClick={stopCamera} title="turn off webcam" style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: 6, padding: "3px 9px", fontFamily: T.mono, fontSize: 10.5, color: T.inkLow, cursor: "pointer", letterSpacing: "0.04em" }} onMouseEnter={e => e.currentTarget.style.color = T.ink} onMouseLeave={e => e.currentTarget.style.color = T.inkLow}>stop</button>
                </>
              )}
            </div>
            {/* 0-3: the camera claim sits next to the control at all times, not
                behind a tooltip and not only once the camera is already on. */}
            <div style={{ fontFamily: T.mono, fontSize: 10, color: T.inkLow, letterSpacing: "0.02em", textAlign: "right", whiteSpace: "nowrap", lineHeight: 1.5 }}>
              {PRIVACY.CAMERA_LOCAL_SHORT}
            </div>
            {status !== "off" && (
              <div style={{ position: "relative", width: 160, height: 120, borderRadius: 8, overflow: "hidden", border: `1px solid ${T.border}`, background: "#000" }}>
                <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)", display: "block", opacity: 0.75 }} />
                <canvas ref={overlayCanvasRef} width={640} height={480} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
              </div>
            )}
          </div>

          {/* place-first: no input bar. a fragment begins by placing it (click the canvas); image/audio still via drag-drop. */}
          {/* 6.1-4. Asleep when this exact arrangement has already been read.
              Session 01 froze the same arrangement five times in seventeen
              seconds: the button gave no sign that pressing it again could not
              produce anything new, so it was pressed again. gen > 0 is what
              keeps the prepared example readable — the seed puts a reading on
              the screen without the visitor having asked for one. */}
          <button onClick={project} disabled={loading || asleep} style={{ position: "absolute", bottom: 18, right: 18, background: (loading || asleep) ? T.bg2 : T.ink, border: `1px solid ${(loading || asleep) ? T.border : T.ink}`, borderRadius: 6, padding: "10px 18px", color: (loading || asleep) ? T.inkFaint : T.bg, fontSize: 12, cursor: (loading || asleep) ? "not-allowed" : "pointer", fontFamily: T.mono, letterSpacing: "0.06em", transition: "all 0.2s", zIndex: 20 }}
            title={asleep ? "the reading on screen is of this arrangement" : undefined}
            onMouseEnter={e => { if (!loading && !asleep) e.currentTarget.style.background = "#F5F3ED"; }} onMouseLeave={e => { if (!loading && !asleep) e.currentTarget.style.background = T.ink; }}>{loading ? "…" : asleep ? "nothing has moved" : "freeze & read →"}</button>

          {/* 3-1 / 3-6. The toast is the event; the line in the reading panel is
              the standing fact. Achromatic on purpose: the arrangement changing
              is the user's doing, and rust belongs to the model (§8). */}
          {frozenToast && (
            <div style={{ position: "absolute", bottom: 70, right: 18, maxWidth: 300, zIndex: 21, background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 6, padding: "10px 14px", fontFamily: T.sans, fontSize: 12, color: T.inkMid, lineHeight: 1.6, animation: "fadeUp 0.35s ease both" }}>
              Current arrangement changed. The selected reading remains tied to its frozen snapshot.
            </div>
          )}

          {/* attribution footer */}
          <a href="https://sujinpark-portfolio.vercel.app" target="_blank" rel="noreferrer" style={{ position: "absolute", bottom: 18, left: 18, fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, textDecoration: "none", zIndex: 14 }} onMouseEnter={e => e.currentTarget.style.color = T.inkMid} onMouseLeave={e => e.currentTarget.style.color = T.inkLow}>Su Jin Park · 2026 · part of ongoing research ↗</a>
        </div>

        <div style={{ width: 1, flexShrink: 0, background: T.border, opacity: 0.5 }} />

        <div style={{ width: 368, flexShrink: 0, background: T.bg2, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
          <div style={{ padding: "14px 28px", borderBottom: `1px solid ${T.borderFaint}`, display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", zIndex: 1 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: T.inkLow, fontFamily: T.mono, letterSpacing: "0.12em" }}>READING</span>
              {/* The reading's language is an input, not something inferred from
                  the fragments. Changing it changes the cache key, so a Korean
                  reading and an English one are two records, not one overwritten. */}
              <select value={readingLanguage} onChange={e => setReadingLanguage(e.target.value)}
                title="The language this reading is written in. Your fragments can be in any language."
                style={{ background: "transparent", border: `1px solid ${T.borderFaint}`, borderRadius: 4, color: T.inkMid, fontFamily: T.mono, fontSize: 10.5, padding: "2px 4px", cursor: "pointer" }}>
                {READING_LANGUAGES.map(l => <option key={l.code} value={l.code} style={{ background: T.bg2, color: T.ink }}>{l.label}</option>)}
              </select>
            </span>
          </div>
          <div style={{ flex: 1, overflowY: "auto", position: "relative", zIndex: 1 }}>
            <OutputPanel pair={pair} loading={loading} mirrorError={mirrorError} gen={gen} frags={frags} onSample={project} frozenChanged={frozenChanged} seeded={seedIsLive} />
          </div>
          {/* Phase 4. Replaces a raw dump of localStorage. That dump was the
              log as it happened to be stored; this is a record with a declared
              format, a stated legacy block, and counts that hold whether or not
              the legacy entries travel.
              It sits under the panel rather than over it: at the top it was a
              second control competing with the comparison, and it is a
              diagnostic. Outside OutputPanel on purpose — import is how a
              record is opened in a fresh profile, and a profile with no reading
              yet renders the empty state, which would put the only way in
              behind the thing it is meant to restore. */}
          <div style={{ padding: "10px 28px", borderTop: `1px solid ${T.borderFaint}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: T.bg2, flexShrink: 0 }}>
            {/* This line said "no reading yet" while a reading was on screen
                and the canvas named the arrangement it came from. It was
                counting COMMITTED readings, which the seed is not one of, and
                it stated that as an absence.
                It reports what is showing now, from the same flag the labels
                use. The seed still does not become a count: it is not the
                visitor's reading and the record does not file it as one. */}
            <span style={{ fontSize: 10.5, color: T.inkLow, fontFamily: T.mono, letterSpacing: "0.06em" }}>
              {gen > 0
                ? `${gen} reading${gen > 1 ? "s" : ""}`
                : pair?.snapshot?.prepared ? "prepared example"
                : pair ? "restored reading"
                : "no reading yet"}
            </span>
            {/* v6.1.5. This said `record`, at 10px in inkLow, with its only
                explanation in a hover title — and S02, S03 and S04 all had to
                be told out loud where it was. Three of three, P01 among them on
                a return visit after finishing S01, is not unfamiliarity.
                The word `export` was the thing missing: it did not appear
                anywhere on screen until this button had already been found and
                pressed, so nobody looking for a way out had anything to look
                for. The title stays (it still names import, which the panel
                also does) but it is no longer carrying the explanation alone —
                a hover title does not exist on touch.
                Once a reading has been committed the button becomes the primary
                one, because that is the moment the visitor has something to
                take with them and the moment all three of them were stuck. It
                stays quiet before then rather than advertising an empty file.
                Safe to change mid-study, unlike the first screen: export
                happens after the freeze and the reading, so the record is
                already fixed by the time anything here is pressed. This is the
                exit, not the entry, and it does not make S05 incomparable. */}
            <button onClick={() => setShowRecord(true)} title="Export or import the reproduction record"
              style={{ background: gen > 0 ? T.ink : "none", border: `1px solid ${gen > 0 ? T.ink : T.border}`, borderRadius: 4, padding: "4px 11px", cursor: "pointer", fontSize: 11.5, color: gen > 0 ? T.bg : T.inkMid, fontFamily: T.mono, letterSpacing: "0.06em", transition: "background 0.15s, color 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.color = gen > 0 ? T.bg : T.ink; e.currentTarget.style.background = gen > 0 ? "#F5F3ED" : "none"; }}
              onMouseLeave={e => { e.currentTarget.style.color = gen > 0 ? T.bg : T.inkMid; e.currentTarget.style.background = gen > 0 ? T.ink : "none"; }}>export ↓</button>
          </div>
        </div>
      </div>
    </div>
  );
}
